import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageHeader } from '../ui/PageHeader';
import { PartnershipsScreen } from './PartnershipsScreen';
import { StatCard } from '../ui/StatCard';
import { ErrorBanner } from '../ui/ErrorBanner';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';
import { supabase } from '../lib/supabase';
import { useToast } from '../lib/useToast';
import { ConfirmModal } from '../components/ConfirmModal';
import { VenuePeopleModal, VenuePersonRow } from '../components/VenuePeopleModal';

/**
 * Venue Portal — the operating console for everything venue-side: cold outreach and the CRM.
 *
 * Organised around the funnel rather than the schema: Find → Add contacts → Review & approve →
 * Sending → Replies. Each stage shows its own work-list and only the actions valid at that stage,
 * so the screen always answers "what do I do next" without Evan having to hold the state machine
 * in his head.
 *
 * Every guardrail rendered here is enforced in Postgres, not in this component — the UI reports
 * the database's decision (`outreach_sending_state`) rather than deciding anything itself.
 */

type OutreachStatus =
  | 'discovered'
  | 'contact_found'
  | 'queued'
  | 'approved'
  | 'sent'
  | 'opened'
  | 'replied'
  | 'claimed'
  | 'failed';

const STATUS_LABEL: Record<OutreachStatus, string> = {
  discovered: 'Needs email',
  contact_found: 'Ready to write',
  queued: 'Awaiting approval',
  approved: 'Approved to send',
  sent: 'Sent',
  opened: 'Opened',
  replied: 'Replied',
  claimed: 'Claimed',
  failed: 'Failed',
};

const STATUS_TONE: Record<OutreachStatus, string> = {
  discovered: 'border-border bg-white/[0.04] text-muted',
  contact_found: 'border-sky-500/40 bg-sky-500/10 text-sky-200',
  queued: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
  approved: 'border-violet-500/40 bg-violet-500/10 text-violet-200',
  sent: 'border-accent/50 bg-accent/15 text-accent',
  opened: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
  replied: 'border-emerald-500/60 bg-emerald-500/20 text-emerald-100',
  claimed: 'border-emerald-400/70 bg-emerald-400/25 text-emerald-50',
  failed: 'border-red-500/40 bg-red-500/10 text-red-200',
};

type Target = {
  id: string;
  venue_id: string;
  venue_name: string | null;
  city_name: string | null;
  address: string | null;
  status: OutreachStatus;
  email: string | null;
  contact_name: string | null;
  contact_source: string | null;
  rater_count: number | null;
  variant: string | null;
  subject: string | null;
  body: string | null;
  batch_id: string | null;
  batch_label: string | null;
  bounced: boolean;
  unsubscribed: boolean;
  bounce_reason: string | null;
  last_error: string | null;
  sent_at: string | null;
  opened_at: string | null;
  replied_at: string | null;
  claimed_at: string | null;
  created_at: string;
};

type Batch = {
  id: string;
  label: string;
  status: string;
  created_at: string;
  approved_at: string | null;
  target_count: number;
  sent_count: number;
  opened_count: number;
  replied_count: number;
  bounced_count: number;
};

type Reply = {
  id: string;
  venue_name: string | null;
  city_name: string | null;
  email: string | null;
  contact_name: string | null;
  subject: string | null;
  replied_at: string | null;
  sent_at: string | null;
  claimed_at: string | null;
};

/**
 * One menu document. A venue has MANY (max 8) — a regular list, a happy-hour card, a food menu.
 * `mime` is optional: the server infers it from the URL extension when the upload didn't report one,
 * so the badge logic here must fall back to the extension too.
 */
type MenuDoc = {
  kind: string | null;
  label?: string | null;
  url: string;
  mime?: string | null;
};

/** Day indices are 0 = Monday … 6 = Sunday, matching the server and the portal editor. */
type HourRow = { d: number; closed?: boolean; open?: string | null; close?: string | null };
type SpecialItem = { text: string; days?: number[] };
type NewsItem = { text: string; date?: string | null; kind?: string | null; options?: string[] };
type HappyHourWindow = {
  days?: number[];
  start?: string | null;
  end?: string | null;
  deals?: string | null;
  /** Derived server-side from the first document in `menus` — kept for older rows. */
  menu_url?: string | null;
  menus?: MenuDoc[];
};

/**
 * The six "door facts" — what someone standing outside needs to know.
 *
 * Every branch is optional on purpose. ABSENT means the venue never answered; it is NOT the same as
 * an explicit answer. `entry.mode === 'none'` is the venue saying "there is no cover and no ticket";
 * no `entry` at all is the venue saying nothing. The review card must show that difference, because
 * approving a stated fact and approving a blank are different acts.
 */
type Essentials = {
  entry?: { mode?: string; cover_usd?: number | null; ticket_url?: string | null; note?: string | null };
  age?: { policy?: string; note?: string | null };
  dress?: { code?: string; note?: string | null };
  reservations?: { accepted?: boolean; url?: string | null; phone?: string | null; note?: string | null };
  payment?: { cards?: boolean; cash_only?: boolean; atm?: boolean; note?: string | null };
  capacity?: { size?: number | null; peak_wait?: string | null; note?: string | null };
};

/**
 * Venue-supplied profile content awaiting review. Untrusted public input — read it before approving.
 *
 * This mirrors `admin_list_venue_profile_submissions` exactly. Anything in this type that the card
 * below does not render is content Evan would be approving sight unseen, which is the whole reason
 * the RPC was widened — so keep the two in step.
 */
type ProfileSubmission = {
  venue_id: string;
  venue_name: string | null;
  city_name: string | null;
  description: string | null;
  highlights: string[] | null;
  menu_url: string | null;
  menus: MenuDoc[] | null;
  photo_urls: string[] | null;
  hours: HourRow[] | null;
  happy_hour: HappyHourWindow[] | null;
  specials: SpecialItem[] | null;
  news: NewsItem[] | null;
  essentials: Essentials | null;
  venue_tags: Record<string, string[]> | null;
  website: string | null;
  /** Per-section switches the VENUE controls. A key that is absent or false means "do not show". */
  visibility: Record<string, boolean> | null;
  notes: string | null;
  rewrite_ok: boolean;
  /** A requested display name, pending Evan's Apply. Never auto-applied. */
  proposed_name: string | null;
  /** Requested location — same contract as the name. Coordinates move the app's map pin on Apply
   * (venues.latitude/longitude is what get_map_venues reads); the address goes to
   * venues.portal_address, which nothing reads yet — stored for whenever we decide to use it. */
  proposed_address: string | null;
  proposed_lat: number | null;
  proposed_lng: number | null;
  current_lat: number | null;
  current_lng: number | null;
  status: string;
  submitted_by: string | null;
  submitted_at: string;
  review_note: string | null;
};

type VenueInterest = {
  venue_id: string;
  venue_name: string | null;
  city_name: string | null;
  topic: string;
  email: string | null;
  note: string | null;
  created_at: string;
};

type PortalAccess = {
  id: string;
  venue_id: string;
  venue_name: string | null;
  city_name: string | null;
  email: string;
  granted_at: string;
  last_seen_at: string | null;
  revoked_at: string | null;
  can_post: boolean;
};

type VenueSignup = {
  id: string;
  email: string;
  phone: string | null;
  venue_name: string | null;
  venue_address: string | null;
  status: string;
  created_at: string;
};

type VenueHit = {
  venue_id: string;
  venue_name: string | null;
  city_name: string | null;
  address: string | null;
  has_access: boolean;
};

type Thread = {
  from_email: string;
  venue_id: string | null;
  venue_name: string | null;
  last_body: string;
  last_at: string;
  unread: number;
  total: number;
  has_help: boolean;
};

type ThreadMsg = { direction: 'in' | 'out'; body: string; at: string; kind: string };

type Claim = {
  id: string;
  venue_name: string | null;
  city_name: string | null;
  contact_name: string;
  contact_email: string;
  role: string | null;
  message: string | null;
  status: string;
  created_at: string;
};

type Counts = {
  by_status: Partial<Record<OutreachStatus, number>>;
  eligible_pool: number;
  suppressed: number;
  sent_total: number;
  sent_last_24h: number;
  bounce_rate: number;
};

type SendingState = {
  can_send: boolean;
  blocked_reason: string | null;
  enabled: boolean;
  daily_cap: number;
  sent_24h: number;
  remaining_today: number;
  bounce_pct: number;
  bounce_window_n: number;
  bounce_threshold: number;
  bounce_paused: boolean;
  paused_reason: string | null;
};

type CityOption = { id: string; name: string };

/**
 * Launch markets (Evan, 2026-08-02): NYC, New Orleans, Ann Arbor — every photo-backed venue,
 * not just rated ones. Brooklyn is a SEPARATE city row from New York City in our data, and Evan
 * confirmed it counts as NYC, so both rows are listed here.
 */
const LAUNCH_CITY_NAMES = ['New York City', 'Brooklyn', 'New Orleans', 'Ann Arbor'];

/**
 * What `outreach-send` reports when asked `{"preflight": true}` — every gate checked, nothing sent.
 */
type Preflight = {
  ready: boolean;
  missing: string[];
  sender: { from: string; reply_to: string; claim_url: string | null; provider: string };
  domain: { domain: string; status: string; ok: boolean; detail?: string };
};

/** Why sending is blocked, in Evan's words rather than the enum's. */
const BLOCKED_COPY: Record<string, { label: string; detail: string }> = {
  kill_switch_off: {
    label: 'Sending is off',
    detail: 'The master switch is off. Nothing can leave the database until you turn it on.',
  },
  bounce_paused: {
    label: 'Paused after a delivery problem',
    detail: 'A bounce or spam complaint tripped the guard. Review before resuming.',
  },
  bounce_rate_exceeded: {
    label: 'Bounce rate too high',
    detail: 'Recent bounces are above the 5% threshold. Sending stopped automatically.',
  },
  daily_cap_reached: {
    label: "Today's cap reached",
    detail: 'The warmup cap for the last 24 hours is used up. Sending resumes as it rolls off.',
  },
  sender_identity_incomplete: {
    label: 'Sender details missing',
    detail: 'A from-address and physical postal address are required before any email can send.',
  },
};

/**
 * Grant a venue portal access by hand. This is the manual path Evan asked for — approving a claim
 * grants access automatically, but for experimenting he needs to hand someone a login without
 * waiting for them to claim anything first.
 */
function AccessGranter({ onGranted }: { onGranted: () => void }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<VenueHit[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [picked, setPicked] = useState<VenueHit | null>(null);
  const [email, setEmail] = useState('');
  /**
   * ⚠️ OPT-IN, AND DEFAULTED ON (Evan 2026-08-17: "I should choose whether or not they get an email").
   * On, because the common case is a venue you have actually spoken to and want to reach; the box is
   * right beside the button so silently granting is one click away when you don't.
   */
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const { showSuccess, showError, toastNode } = useToast();

  /**
   * ⚠️ THE ERROR USED TO BE DISCARDED — `const { data } = await …` with no `error`. Any failure
   * (forbidden, a bad param, a network blip) produced an empty dropdown that looked exactly like
   * "no such venue", which is why this read as broken with the venue sitting right there. It is
   * surfaced now.
   *
   * ⚠️ SEQUENCED, because this fires on every keystroke. Without the guard a slow early request
   * ("br") can resolve AFTER a later one and overwrite the results for what you actually typed.
   */
  const searchSeq = useRef(0);
  async function search(next: string) {
    setQ(next);
    setPicked(null);
    setSearchError(null);
    const seq = searchSeq.current + 1;
    searchSeq.current = seq;
    if (next.trim().length < 2) {
      setHits([]);
      return;
    }
    const { data, error } = await supabase.rpc('admin_search_venues_for_portal', {
      p_q: next.trim(),
      p_limit: 8,
    });
    if (searchSeq.current !== seq) return;
    if (error) {
      setHits([]);
      setSearchError(error.message);
      return;
    }
    setHits((data as VenueHit[]) ?? []);
  }

  async function grant() {
    const clean = email.trim().toLowerCase();
    if (!picked) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
      showError('Enter a valid email address');
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.rpc('admin_grant_venue_portal_access', {
        p_venue_id: picked.venue_id,
        p_email: clean,
        p_claim_id: null,
      });
      if (error) throw error;

      /**
       * ⚠️ THE GRANT IS THE SUCCESS; THE EMAIL IS BEST-EFFORT. If Resend rejects (unverified domain
       * is the likely one) the access still stands and the toast says so, rather than making the
       * whole action look like it failed and inviting a second grant.
       */
      let inviteNote = '';
      if (sendInvite) {
        try {
          await sendVenueInvite(picked.venue_id, clean);
          inviteNote = ' — invite emailed';
        } catch (e) {
          inviteNote = ` — access granted, but the invite email FAILED: ${
            e instanceof Error ? e.message : 'unknown error'
          }`;
        }
      }
      showSuccess(`${clean} can now sign in as ${picked.venue_name ?? 'that venue'}${inviteNote}`);
      setQ('');
      setHits([]);
      setPicked(null);
      setEmail('');
      onGranted();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Could not grant access');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-xl border border-accent/40 bg-accent/[0.06] p-4">
      {toastNode}
      <div className="admin-label-primary mb-1 text-[15px]">Activate a venue</div>
      <p className="mb-3 text-[11px] leading-relaxed text-mutedDim">
        Search the venue, put in the owner or manager&apos;s email, hit Grant. They sign in at{' '}
        <span className="text-muted">portal.joinshado.com</span> with a 6-digit code sent to that
        address — no password, nothing to claim first.
        {' '}
        <span className="text-amber-200">
          Granting sends them nothing, so you still have to tell them.
        </span>
      </p>

      <div className="flex flex-wrap items-start gap-2">
        <div className="relative min-w-[240px] flex-1">
          <input
            value={picked ? (picked.venue_name ?? '') : q}
            onChange={(e) => void search(e.target.value)}
            placeholder="Search a venue…"
            className="admin-input w-full text-xs"
          />
          {searchError ? (
            <p className="mt-1 text-[11px] text-red-400">Search failed: {searchError}</p>
          ) : null}
          {!searchError && !picked && q.trim().length >= 2 && hits.length === 0 ? (
            <p className="mt-1 text-[11px] text-mutedDim">No venues match “{q.trim()}”.</p>
          ) : null}
          {hits.length > 0 && !picked ? (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-border bg-surfaceElevated shadow-lg">
              {hits.map((h) => (
                <button
                  key={h.venue_id}
                  type="button"
                  onClick={() => {
                    setPicked(h);
                    setHits([]);
                  }}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-white/[0.05]"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[12px] text-white">{h.venue_name}</span>
                    <span className="block truncate text-[10px] text-mutedDim">
                      {[h.city_name, h.address].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {h.has_access ? (
                    <span className="shrink-0 text-[9px] uppercase tracking-wide text-accent">has access</span>
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="owner@venue.com"
          className="admin-input min-w-[200px] flex-1 text-xs"
        />
        <label className="flex shrink-0 cursor-pointer items-center gap-2 text-[11px] text-muted">
          <input
            type="checkbox"
            checked={sendInvite}
            onChange={(e) => setSendInvite(e.target.checked)}
          />
          Email them an invite
        </label>
        <button
          type="button"
          disabled={busy || !picked || !email.trim()}
          onClick={() => void grant()}
          className="admin-btn admin-btn-primary admin-btn-sm"
        >
          {busy ? 'Granting…' : 'Grant access'}
        </button>
      </div>
    </div>
  );
}

/**
 * Sends the branded "you have access" email. The function refuses any address that does not already
 * hold live access, so this can never promise a login that does not exist.
 */
async function sendVenueInvite(venueId: string, email: string): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('Not signed in');
  const { data, error } = await supabase.functions.invoke('venue-portal-invite', {
    body: { venueId, email },
    headers: { Authorization: `Bearer ${token}` },
  });
  if (error) {
    // The Edge Function returns its reason in the body; surface that rather than "non-2xx".
    const detail = (data as { error?: string } | null)?.error;
    throw new Error(detail || error.message);
  }
  const res = data as { ok?: boolean; error?: string } | null;
  if (!res?.ok) throw new Error(res?.error ?? 'Invite failed');
}

function StatusPill({ status }: { status: OutreachStatus }) {
  return (
    <span
      className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] ${STATUS_TONE[status]}`}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

/** Haversine, formatted for a sanity read: "12 m" is a doorway fix, "3.4 km" is suspicious. */
function formatPinDistance(lat1: number, lng1: number, lat2: number, lng2: number): string {
  const rad = Math.PI / 180;
  const a =
    Math.sin(((lat2 - lat1) * rad) / 2) ** 2 +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(((lng2 - lng1) * rad) / 2) ** 2;
  const meters = 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;
}

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/* ───────────────────────── Venue-written content: formatting ─────────────────────────
 *
 * Everything below turns the raw submission JSON into something readable at a glance. It is all
 * pure formatting — the server owns validation, and this file must never contradict it.
 *
 * Two rules run through the whole thing:
 *  1. Every string is UNTRUSTED PUBLIC INPUT written by a venue. It is rendered as text, never as
 *     HTML, and every URL goes through `safeHref` so a `javascript:` link can't be clicked.
 *  2. "Not set" is a real, distinct answer. Blanks are rendered rather than skipped wherever the
 *     absence is meaningful (the six essentials, each day of the week).
 */

/** Monday-first: a bar's week runs toward the weekend, not from it. */
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const ENTRY_MODE_LABEL: Record<string, string> = {
  none: 'No cover, no ticket',
  cover: 'Cover charge',
  ticket: 'Ticketed',
  varies: 'Varies by night',
};

const AGE_POLICY_LABEL: Record<string, string> = {
  all_ages: 'All ages',
  '18_plus': '18+',
  '21_plus': '21+',
  varies: 'Varies by night',
};

const DRESS_CODE_LABEL: Record<string, string> = {
  none: 'No dress code',
  casual: 'Casual dress',
  smart_casual: 'Smart casual',
  upscale: 'Upscale',
};

const PEAK_WAIT_LABEL: Record<string, string> = {
  none: 'No wait at peak',
  under_15: 'Peak wait under 15 min',
  '15_30': 'Peak wait 15–30 min',
  '30_plus': 'Peak wait 30+ min',
};

const MENU_KIND_LABEL: Record<string, string> = {
  regular: 'Regular',
  happy_hour: 'Happy hour',
  specials: 'Specials',
  food: 'Food',
  drinks: 'Drinks',
  events: 'Events',
};

/**
 * The sections the app can actually receive, in the order they read on the card.
 *
 * ⚠️ THIS LIST IS THE CONTRACT WITH PRODUCTION — re-derived 2026-08-16 from
 * `pg_get_functiondef('public.get_venue_public_profile')`. That function returns exactly
 * description, website, menu_url + menus (both on the one `menu` switch), essentials, photo_urls,
 * hours, happy_hour, specials and news, each gated on
 * `COALESCE((visibility ->> key)::boolean, false)` — so absent === hidden.
 *
 * `highlights` and `venue_tags` are deliberately ABSENT. The portal stores them and the portal has a
 * `venue_tags` visibility switch, but the public RPC never returns either one and no file under
 * `app/`, `components/`, `lib/` or `screens/` reads them. They therefore cannot be described as
 * "published" or as "hidden by the venue" — both would be lies. If a future migration adds them to
 * `get_venue_public_profile`, add the key here and drop the `notInApp` flag on those two blocks.
 */
const PUBLISHABLE_SECTIONS = [
  'description',
  'essentials',
  'hours',
  'happy_hour',
  'menu',
  'specials',
  'news',
  'photos',
  'website',
] as const;

/** Lower-case noun for a section, used inside the "Approving publishes: …" sentence. */
const SECTION_NOUN: Record<string, string> = {
  description: 'description',
  essentials: 'essentials',
  hours: 'hours',
  happy_hour: 'happy hour',
  menu: 'menus',
  specials: 'specials',
  news: 'posts',
  photos: 'photos',
  website: 'website',
};

/**
 * Enum → label, falling back to the raw value so an unrecognised code is visible rather than
 * silently blank. `hasOwnProperty` rather than a bare index so `__proto__`-style keys can't reach
 * anything off the prototype chain.
 */
function labelFor(map: Record<string, string>, key: string | null | undefined, fallback: string): string {
  const k = (key ?? '').trim();
  if (!k) return fallback;
  return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : k;
}

/** jsonb can arrive as null or as something that isn't an array. Never map over it blind. */
function asArray<T>(v: T[] | null | undefined): T[] {
  return Array.isArray(v) ? v : [];
}

/**
 * Only http(s) becomes a clickable link. A venue types these, so `javascript:` and `data:` are
 * refused outright rather than sanitised.
 */
function safeHref(url: string | null | undefined): string | null {
  const raw = (url ?? '').trim();
  if (!raw) return null;
  return /^https?:\/\//i.test(raw) ? raw : null;
}

/** "22:30" → "10:30 PM". Null for anything unparseable, so callers can say "not set" instead. */
function prettyTime(hhmm: string | null | undefined): string | null {
  if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m) || h > 23 || m > 59) return null;
  const suffix = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${suffix}` : `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/**
 * A cover price as a price. The server takes `^\d{1,4}(\.\d{1,2})?$` and stores it `::numeric`, so a
 * $12.50 cover round-trips through JSON as the number `12.5` — printing it raw gives "$12.5", which
 * reads as a typo rather than a price. Whole numbers stay whole: "$12", not "$12.00".
 */
function formatUsd(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/** "Mon–Fri" beats "Mon, Tue, Wed, Thu, Fri". Collapses only genuine runs of three or more. */
function summarizeDays(days: number[] | null | undefined): string {
  const sorted = Array.from(new Set(asArray(days)))
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
    .sort((a, b) => a - b);
  if (sorted.length === 0) return 'No days set';
  if (sorted.length === 7) return 'Every day';

  const runs: number[][] = [];
  sorted.forEach((d) => {
    const last = runs[runs.length - 1];
    if (last && d === last[last.length - 1] + 1) last.push(d);
    else runs.push([d]);
  });
  return runs
    .map((r) =>
      r.length >= 3 ? `${DAY_LABELS[r[0]]}–${DAY_LABELS[r[r.length - 1]]}` : r.map((d) => DAY_LABELS[d]).join(', '),
    )
    .join(', ');
}

/**
 * PDF or image — the reviewer needs to know what a link will open before clicking it.
 *
 * Null-tolerant on purpose: `MenuList` guards every other field access with `m?.`, and a helper that
 * dereferenced `m` directly would throw on the one input the surrounding guards exist to survive.
 */
function menuFormat(m: MenuDoc | null | undefined): string {
  const mime = (m?.mime ?? '').trim().toLowerCase();
  if (mime === 'application/pdf') return 'PDF';
  if (mime.indexOf('image/') === 0) return 'Image';
  const path = (m?.url ?? '').toLowerCase().split('?')[0];
  if (/\.pdf$/.test(path)) return 'PDF';
  if (/\.(png|jpg|jpeg|webp|gif|heic|heif)$/.test(path)) return 'Image';
  return 'File';
}

/** A venue-supplied URL. Unsafe ones render as inert text so nothing silently disappears. */
function ExtLink({
  href,
  children,
  className,
}: {
  href: string | null | undefined;
  children: React.ReactNode;
  className?: string;
}) {
  const safe = safeHref(href);
  if (!safe) {
    return <span className="text-[11px] text-mutedDim">{children} (unusable URL)</span>;
  }
  return (
    <a
      href={safe}
      target="_blank"
      rel="noreferrer"
      className={className ?? 'text-[12px] text-accent underline underline-offset-2'}
    >
      {children}
    </a>
  );
}

/**
 * One labelled group inside the review card.
 *
 * `hidden` is the VENUE's own switch, not ours. It sits on the label rather than in a footnote
 * because approving content that will appear and approving content the venue has turned off are
 * different decisions. Pass `undefined` for fields with no visibility switch at all.
 *
 * `notInApp` is a third state and NOT the same as `hidden`: the section is stored, but no app
 * surface reads it whatever the switch says (highlights and tags — see PUBLISHABLE_SECTIONS).
 * A "hidden in app" badge there would imply flipping a switch makes it appear, which is false.
 */
function ReviewBlock({
  label,
  hidden,
  notInApp,
  children,
}: {
  label: string;
  hidden?: boolean;
  notInApp?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-3">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-mutedDim">{label}</span>
        {notInApp === true ? (
          <span
            className="rounded-full border border-border bg-white/[0.04] px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-mutedDim"
            title="Stored on the submission, but get_venue_public_profile does not return this and no app screen reads it."
          >
            not read by the app
          </span>
        ) : hidden === true ? (
          <span className="rounded-full border border-border bg-white/[0.04] px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-mutedDim">
            hidden in app
          </span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/** One of the six door facts. Renders even when unanswered — the blank is the information. */
function Fact({ label, value, note }: { label: string; value: React.ReactNode; note?: string | null }) {
  return (
    <div className="rounded-lg border border-border bg-white/[0.03] px-3 py-2">
      <div className="text-[9px] font-semibold uppercase tracking-[0.08em] text-mutedDim">{label}</div>
      <div className="mt-0.5 text-[12px] leading-snug text-muted">{value}</div>
      {note ? <div className="mt-0.5 text-[11px] leading-snug text-mutedDim">{note}</div> : null}
    </div>
  );
}

/** The venue never answered this question — the blank is the information. */
const NOT_SET = <span className="text-mutedDim">Not set</span>;

/**
 * The venue DID answer, but nothing renderable came of it — so the app will draw NO row here.
 *
 * ⚠️ THIS IS NOW A LEGACY-ONLY BRANCH, AND THE OLD DOC HERE WAS WRONG. It used to cite
 * `payment: {cash_only: false}` as "the real case" and claim the app renders nothing for it. The app
 * renders "Not cash only" for that payload (see `paymentLine` below, mirrored from
 * `components/VenueOwnProfileBlock.tsx`), and every other stored payment shape prints something too,
 * because `venue_portal_save_profile` refuses to store a `payment` object unless at least one of
 * cards/cash_only/atm is a boolean. The same is true of the other five facts: each needs its own
 * value key to be stored at all, so no CURRENT save path reaches this string.
 *
 * It stays because a row written by an older build — or edited by hand in SQL — can still hold an
 * object with no renderable key, and "Not set" would be a lie about that row: "Not set" is this
 * card's promise that the venue was never asked-and-answered. The two must never be conflated.
 */
const ANSWERED_BLANK = (
  <span className="text-mutedDim" title="The venue answered, but the answer has nothing to display. The app shows no row here.">
    Answered · nothing to show
  </span>
);

/** The note underneath is the whole answer. The app renders a note-only row the same way. */
const NOTE_ONLY = <span className="text-mutedDim">See note</span>;

/**
 * What a door fact's value line says.
 *
 * `answered` = the venue touched this key at all. `content` = something renderable came of it.
 * Kept in one place so all six facts tell the same story about silence.
 */
function factValue(
  answered: boolean,
  content: React.ReactNode | null,
  note?: string | null,
): React.ReactNode {
  if (content) return content;
  if ((note ?? '').trim()) return NOTE_ONLY;
  return answered ? ANSWERED_BLANK : NOT_SET;
}

/* ═══ PAYMENT — ONE MODEL, THREE IDENTICAL COPIES ══════════════════════════════════════════════
   `cards` and `cash_only` are NOT two independent facts. They are ONE question — "what can I pay
   with?" — stored in two jsonb keys only because `venue_portal_save_profile` is live in production
   and its contract (the ARRAY['cards','cash_only','atm'] loop, plus "cash_only=true forces
   cards=false") cannot change under it.

   Modelling them as two independent tri-states is what let a venue store {cards:false,
   cash_only:false} — two clicks in the portal, and a state that means nothing — and it let the three
   surfaces name the same stored answer differently: the app printed "Cash only" for that payload, a
   fact the venue had explicitly answered NO to, while the review card printed "Not set".

   The fix is the MODEL, not the renderers:
     • the portal now writes the pair TOGETHER from one mutually-exclusive control, so a venue can no
       longer select a contradictory or unrenderable state;
     • all three surfaces read the pair through THIS derivation, so what the venue picked, what a
       customer reads, and what Evan approves are the same words.

   ⚠️ THREE COPIES — EDIT ALL THREE OR NONE. There is no shared module to import: the app is React
   Native, the portal is a separate Vite app, and shado-admin is a SEPARATE GIT SUBMODULE that has to
   build on its own. The copies, all byte-identical apart from quote style:
       components/VenueOwnProfileBlock.tsx            (what a customer reads)
       venue-portal/src/ProfileFields.tsx             (what a venue picks)
       shado-admin/src/pages/VenueOutreachScreen.tsx  ← you are here (what Evan approves)

   ⚠️ ALL 27 (cards, cash_only, atm) COMBINATIONS RESOLVE HERE, and none of them asserts anything the
   venue did not say. Only two method states are writable today; the other two exist because rows
   saved before the single control shipped still hold them:
       cash_only = true                    → "Cash only"      (writable; server forces cards=false)
       cards = true                        → "Cards accepted" (writable; always paired cash_only=false)
       cards = false, cash_only ≠ true     → "No cards"       (legacy: they said no cards — NOT that
                                                               cash is the only way in)
       cash_only = false, cards unanswered → "Not cash only"  (legacy: literally all they said)
       neither key answered                → nothing at all
   "Cards only" is deliberately NOT offered: with only `cards` and `cash_only` there is no key for
   "we do not take cash", so it would be stored — and therefore read back — exactly like "Cards
   accepted". Inventing a key would change the production contract.
   ═════════════════════════════════════════════════════════════════════════════════════════════ */

type PaymentAnswer = { cards?: boolean | null; cash_only?: boolean | null; atm?: boolean | null } | null | undefined;

type PaymentMethod = 'cash_only' | 'cards' | 'no_cards' | 'not_cash_only' | null;

const PAYMENT_METHOD_LABELS: Record<Exclude<PaymentMethod, null>, string> = {
  cash_only: 'Cash only',
  cards: 'Cards accepted',
  no_cards: 'No cards',
  not_cash_only: 'Not cash only',
};

const ATM_YES_LABEL = 'ATM on site';
const ATM_NO_LABEL = 'No ATM on site';

/**
 * The single answer the `cards`/`cash_only` pair carries, or null when neither was answered.
 *
 * The order is the resolution order and it is load-bearing: `cash_only` is read first so the
 * door-safe reading wins for {cards: true, cash_only: true} — a shape `venue_portal_save_profile`
 * cannot produce (it forces cards=false) but a hand-edited row could.
 */
export function paymentMethod(pay: PaymentAnswer): PaymentMethod {
  if (pay?.cash_only === true) return 'cash_only';
  if (pay?.cards === true) return 'cards';
  if (pay?.cards === false) return 'no_cards';
  if (pay?.cash_only === false) return 'not_cash_only';
  return null;
}

/**
 * The whole Payment value line — method, then ATM — or '' when the venue answered none of the three.
 *
 * '' is only ever the ALL-UNANSWERED case, and the server never stores a `payment` object without at
 * least one of the three booleans, so any stored payment answer prints something on every surface.
 */
export function paymentLine(pay: PaymentAnswer): string {
  const method = paymentMethod(pay);
  const parts: string[] = [];
  if (method) parts.push(PAYMENT_METHOD_LABELS[method]);
  if (pay?.atm === true) parts.push(ATM_YES_LABEL);
  else if (pay?.atm === false) parts.push(ATM_NO_LABEL);
  return parts.join(' · ');
}

/** All six, always, in the order someone approaching a door would ask them. */
function EssentialsGrid({ essentials }: { essentials: Essentials | null }) {
  const entry = essentials?.entry;
  const age = essentials?.age;
  const dress = essentials?.dress;
  const res = essentials?.reservations;
  const pay = essentials?.payment;
  const cap = essentials?.capacity;

  let entryContent: React.ReactNode = null;
  if (entry?.mode) {
    const cover =
      entry.mode === 'cover' && typeof entry.cover_usd === 'number' && Number.isFinite(entry.cover_usd)
        ? ` · $${formatUsd(entry.cover_usd)}`
        : '';
    entryContent = (
      <>
        {labelFor(ENTRY_MODE_LABEL, entry.mode, 'Set')}
        {cover}
        {entry.mode === 'ticket' && entry.ticket_url ? (
          <>
            {' · '}
            <ExtLink href={entry.ticket_url}>ticket link</ExtLink>
          </>
        ) : null}
      </>
    );
  }

  let resContent: React.ReactNode = null;
  if (typeof res?.accepted === 'boolean') {
    resContent = res.accepted ? (
      <>
        Accepted
        {res.url ? (
          <>
            {' · '}
            <ExtLink href={res.url}>booking link</ExtLink>
          </>
        ) : null}
        {res.phone ? ` · ${res.phone}` : ''}
      </>
    ) : (
      'Walk-in only'
    );
  }

  // ⚠️ THE SAME `paymentLine` THE APP RUNS, not a paraphrase of it. This card used to re-derive the
  // payment wording by hand and got three states wrong — {cash_only:false}, {atm:false} and the two
  // together all read "Answered · nothing to show" here while the app printed real text — so Evan
  // was approving a Payment line he had never been shown. Anything about how the pair is read
  // belongs in `paymentLine` above, where all three surfaces share it.
  const payLine = paymentLine(pay);

  const capBits: string[] = [];
  // The app requires a positive size before it prints one; "Holds ~0" would be a claim, not a fact.
  if (typeof cap?.size === 'number' && Number.isFinite(cap.size) && cap.size > 0) {
    capBits.push(`Holds ~${Math.round(cap.size)}`);
  }
  if (cap?.peak_wait) capBits.push(labelFor(PEAK_WAIT_LABEL, cap.peak_wait, cap.peak_wait));

  return (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
      <Fact label="Entry" value={factValue(Boolean(entry), entryContent, entry?.note)} note={entry?.note} />
      <Fact
        label="Age"
        value={factValue(
          Boolean(age),
          age?.policy ? labelFor(AGE_POLICY_LABEL, age.policy, age.policy) : null,
          age?.note,
        )}
        note={age?.note}
      />
      <Fact
        label="Dress"
        value={factValue(
          Boolean(dress),
          dress?.code ? labelFor(DRESS_CODE_LABEL, dress.code, dress.code) : null,
          dress?.note,
        )}
        note={dress?.note}
      />
      <Fact label="Reservations" value={factValue(Boolean(res), resContent, res?.note)} note={res?.note} />
      <Fact
        label="Payment"
        value={factValue(Boolean(pay), payLine || null, pay?.note)}
        note={pay?.note}
      />
      <Fact
        label="Capacity"
        value={factValue(Boolean(cap), capBits.length ? capBits.join(' · ') : null, cap?.note)}
        note={cap?.note}
      />
    </div>
  );
}

/** One stored row rendered as a phrase. Exported from the table only for readability. */
function describeHourRow(row: HourRow | undefined): string {
  if (row?.closed === true) return 'Closed';
  const open = prettyTime(row?.open);
  const close = prettyTime(row?.close);
  if (open && close) return `${open} – ${close}`;
  if (open) return `From ${open}`;
  if (close) return `Until ${close}`;
  return 'Not set';
}

/** Seven rows always. A day the venue never filled in reads "Not set", not "Closed". */
function HoursTable({ rows }: { rows: HourRow[] }) {
  /**
   * GROUP, never overwrite. `venue_portal_save_profile` aggregates with `jsonb_agg(DISTINCT …)`,
   * which is distinct on the WHOLE object rather than on `d` — so two rows for the same day with
   * different times both persist. Keeping only one (the old `byDay.set(r.d, r)`) would hide half of
   * what approving actually publishes, silently, on the one screen whose job is to show everything.
   */
  const byDay = new Map<number, HourRow[]>();
  rows.forEach((r) => {
    if (r && Number.isInteger(r.d) && r.d >= 0 && r.d <= 6) {
      const existing = byDay.get(r.d);
      if (existing) existing.push(r);
      else byDay.set(r.d, [r]);
    }
  });

  return (
    <div className="rounded-lg border border-border bg-white/[0.03] px-3 py-1.5">
      {DAY_LABELS.map((day, d) => {
        const dayRows = byDay.get(d) ?? [];
        const values = dayRows.length > 0 ? dayRows.map(describeHourRow) : ['Not set'];
        return (
          <div key={d} className="flex items-baseline justify-between gap-4 py-0.5">
            <span className="w-10 shrink-0 text-[11px] uppercase tracking-wide text-mutedDim">{day}</span>
            <span className="min-w-0 text-right">
              {values.map((value, i) => (
                <span
                  key={`${d}-${i}`}
                  className={`block text-[12px] tabular-nums ${
                    value === 'Not set' ? 'text-mutedDim' : 'text-muted'
                  }`}
                >
                  {value}
                </span>
              ))}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** The venue's menu documents — many, each labelled by kind and by what it will open as. */
function MenuList({ docs }: { docs: MenuDoc[] }) {
  return (
    <div className="flex flex-col gap-1">
      {docs.map((m, i) => (
        <div
          key={`${m?.url ?? 'doc'}-${i}`}
          className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-white/[0.03] px-3 py-1.5"
        >
          <span className="rounded-full border border-border bg-white/[0.04] px-2 py-0.5 text-[9px] uppercase tracking-wide text-mutedDim">
            {labelFor(MENU_KIND_LABEL, m?.kind, 'Menu')}
          </span>
          <span className="text-[10px] uppercase tracking-wide text-mutedDim">{menuFormat(m)}</span>
          <ExtLink href={m?.url} className="min-w-0 text-[12px] text-accent underline underline-offset-2">
            {(m?.label ?? '').trim() || 'Open document'}
          </ExtLink>
        </div>
      ))}
    </div>
  );
}

/**
 * A single submission, rendered in full.
 *
 * Before 2026-08-16 this card showed description, highlights, photos and one menu link — hours,
 * happy hour, specials, posts, website, tags, notes, essentials and the menu list were approved
 * sight unseen. Everything the RPC returns is now on screen, so "Approve" means Evan has seen it.
 *
 * Seeing it is not the same as knowing what approving it DOES, so the first band under the header
 * states the consequence in one sentence — what publishes, what the venue has switched off, and what
 * no app screen reads. It is computed from `visibility` over PUBLISHABLE_SECTIONS, i.e. from exactly
 * the predicate `get_venue_public_profile` applies, so the sentence cannot drift from the outcome.
 */
function ProfileReviewCard({
  p,
  busy,
  onApprove,
  onReject,
  visOverride,
  hideActions,
}: {
  p: ProfileSubmission;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  /**
   * The reviewer's PENDING choice of what goes live, when this card is inside the CRM drawer. The
   * card must show the effect of ticking a box immediately — reading `p.visibility` there would keep
   * describing the venue's original answer while the reviewer edits it.
   */
  visOverride?: Record<string, boolean>;
  /** The drawer owns Approve/Reject, so the card must not render a second pair. */
  hideActions?: boolean;
}) {
  const vis = visOverride ?? p.visibility ?? {};
  /** The venue's switches default to OFF server-side, so absent === hidden. */
  const shown = (key: string) => vis[key] === true;

  const highlights = asArray(p.highlights);
  const menus = asArray(p.menus);
  const photos = asArray(p.photo_urls);
  const hours = asArray(p.hours);
  const happyHour = asArray(p.happy_hour);
  const specials = asArray(p.specials);
  const news = asArray(p.news);
  const tagEntries = Object.entries(p.venue_tags ?? {}).filter(([, tags]) => asArray(tags).length > 0);

  const hasEssentials = Object.keys(p.essentials ?? {}).length > 0;
  const hasMenus = menus.length > 0 || Boolean(p.menu_url);
  const menuCount = menus.length > 0 ? menus.length : p.menu_url ? 1 : 0;

  /**
   * Does this section have content at all? A switch off over nothing isn't worth flagging.
   *
   * The emptiness tests mirror `get_venue_public_profile`: it wraps text in `NULLIF(btrim(x), '')`
   * and arrays in `NULLIF(x, '[]')`, so a whitespace-only description publishes nothing and must not
   * be counted here either.
   */
  const filled: Record<string, boolean> = {
    description: (p.description ?? '').trim().length > 0,
    essentials: hasEssentials,
    hours: hours.length > 0,
    happy_hour: happyHour.length > 0,
    menu: hasMenus,
    specials: specials.length > 0,
    news: news.length > 0,
    photos: photos.length > 0,
    website: (p.website ?? '').trim().length > 0,
  };

  /** "3 menus" beats "menus" — Evan can check the number against what he just scrolled past. */
  const plural = (n: number, one: string, many: string) => (n === 1 ? `1 ${one}` : `${n} ${many}`);
  const sectionName = (key: string): string => {
    switch (key) {
      case 'menu':
        return plural(menuCount, 'menu', 'menus');
      case 'photos':
        return plural(photos.length, 'photo', 'photos');
      case 'specials':
        return plural(specials.length, 'special', 'specials');
      case 'news':
        return plural(news.length, 'post', 'posts');
      case 'happy_hour':
        return happyHour.length > 1 ? `${happyHour.length} happy-hour windows` : 'happy hour';
      default:
        return SECTION_NOUN[key] ?? key;
    }
  };

  const publishNames = PUBLISHABLE_SECTIONS.filter((k) => filled[k] && shown(k)).map(sectionName);
  const hiddenNames = PUBLISHABLE_SECTIONS.filter((k) => filled[k] && !shown(k)).map(sectionName);

  /** Stored by the portal, returned by no RPC, read by no screen — see PUBLISHABLE_SECTIONS. */
  const unusedNames: string[] = [];
  if (highlights.length > 0) unusedNames.push(plural(highlights.length, 'highlight', 'highlights'));
  if (tagEntries.length > 0) unusedNames.push('tags');

  const isEmpty =
    !PUBLISHABLE_SECTIONS.some((k) => filled[k]) && highlights.length === 0 && tagEntries.length === 0;

  return (
    <div className="rounded-xl border border-amber-500/35 bg-amber-500/[0.05] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="admin-label-primary truncate text-[14px]">
            {p.venue_name ?? '—'}
            {p.city_name ? (
              <span className="ml-2 text-[11px] font-normal text-mutedDim">{p.city_name}</span>
            ) : null}
          </div>
          <div className="mt-0.5 text-[11px] text-mutedDim">
            {p.submitted_by ?? 'unknown'} · {timeAgo(p.submitted_at)}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <span
            className={`rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-wide ${
              p.rewrite_ok
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                : 'border-border bg-white/[0.04] text-mutedDim'
            }`}
            title={
              p.rewrite_ok
                ? 'The venue is happy for you to reword their copy before it goes live.'
                : 'The venue asked for their wording to be published as written.'
            }
          >
            {p.rewrite_ok ? 'OK to rewrite' : 'Publish as written'}
          </span>
          {hideActions ? null : (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={onApprove}
                className="admin-btn admin-btn-primary admin-btn-sm"
              >
                Approve
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={onReject}
                className="text-[11px] text-mutedDim transition-colors hover:text-red-300"
              >
                Reject
              </button>
            </>
          )}
        </div>
      </div>

      {p.review_note ? (
        <div className="mt-3 rounded-lg border border-red-500/25 bg-red-500/[0.06] px-3 py-2 text-[11px] leading-relaxed text-red-200">
          <span className="font-semibold">Last review note:</span> {p.review_note}
        </div>
      ) : null}

      {/*
        What approving this row actually DOES, in one band, before any content is read.
        Derived from the same `visibility` map `get_venue_public_profile` gates on, over the same
        section list that function returns — so it cannot promise something the app will not show.
      */}
      {isEmpty ? null : (
        <div className="mt-3 rounded-lg border border-accent/40 bg-accent/[0.08] px-3 py-2 text-[12px] leading-relaxed text-muted">
          <span className="font-semibold text-white">Approving publishes:</span>{' '}
          {publishNames.length > 0
            ? `${publishNames.join(', ')}.`
            : hiddenNames.length > 0
              ? 'nothing — every section the venue filled in is switched off.'
              : 'nothing the app reads.'}
          {hiddenNames.length > 0 ? (
            <>
              {' '}
              <span className="font-semibold text-amber-200">Hidden by the venue:</span>{' '}
              {hiddenNames.join(', ')} — approving will not make these appear.
            </>
          ) : null}
          {unusedNames.length > 0 ? (
            <>
              {' '}
              <span className="font-semibold text-mutedDim">Stored but unused:</span>{' '}
              {unusedNames.join(', ')} — no app screen reads these yet.
            </>
          ) : null}
        </div>
      )}

      {isEmpty ? (
        <div className="mt-3 rounded-lg border border-border bg-white/[0.03] px-3 py-2 text-[12px] text-mutedDim">
          {p.notes
            ? 'Nothing submitted except the private notes below.'
            : 'Nothing submitted — this row is entirely blank.'}
        </div>
      ) : null}

      {filled.description ? (
        <ReviewBlock label="Description" hidden={!shown('description')}>
          <p className="whitespace-pre-wrap rounded-lg border border-border bg-white/[0.03] px-3 py-2 text-[12px] leading-relaxed text-muted">
            {p.description}
          </p>
        </ReviewBlock>
      ) : null}

      {highlights.length > 0 ? (
        <ReviewBlock label="Highlights" notInApp>
          <div className="flex flex-wrap gap-1.5">
            {highlights.map((h, i) => (
              <span
                key={`${h}-${i}`}
                className="rounded-full border border-border bg-white/[0.04] px-2.5 py-1 text-[11px] text-muted"
              >
                {h}
              </span>
            ))}
          </div>
        </ReviewBlock>
      ) : null}

      {hasEssentials ? (
        <ReviewBlock label="Essentials" hidden={!shown('essentials')}>
          <EssentialsGrid essentials={p.essentials} />
        </ReviewBlock>
      ) : null}

      {hours.length > 0 ? (
        <ReviewBlock label="Hours" hidden={!shown('hours')}>
          <HoursTable rows={hours} />
        </ReviewBlock>
      ) : null}

      {happyHour.length > 0 ? (
        <ReviewBlock label="Happy hour" hidden={!shown('happy_hour')}>
          <div className="flex flex-col gap-1.5">
            {happyHour.map((w, i) => {
              const start = prettyTime(w?.start);
              const end = prettyTime(w?.end);
              const time = start && end ? `${start} – ${end}` : start ?? end ?? 'No time set';
              const windowMenus = asArray(w?.menus);
              return (
                <div
                  key={`hh-${i}`}
                  className="rounded-lg border border-border bg-white/[0.03] px-3 py-2"
                >
                  <div className="text-[12px] text-muted">
                    <span className="uppercase tracking-wide text-mutedDim">{summarizeDays(w?.days)}</span>
                    <span className="mx-1.5 text-mutedDim">·</span>
                    <span className="tabular-nums">{time}</span>
                  </div>
                  {w?.deals ? (
                    <div className="mt-1 whitespace-pre-wrap text-[12px] leading-relaxed text-muted">
                      {w.deals}
                    </div>
                  ) : null}
                  {windowMenus.length > 0 ? (
                    <div className="mt-1.5">
                      <MenuList docs={windowMenus} />
                    </div>
                  ) : w?.menu_url ? (
                    <div className="mt-1.5">
                      <ExtLink href={w.menu_url}>Happy-hour menu</ExtLink>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </ReviewBlock>
      ) : null}

      {hasMenus ? (
        <ReviewBlock label={`Menus${menus.length > 1 ? ` · ${menus.length}` : ''}`} hidden={!shown('menu')}>
          {menus.length > 0 ? (
            <MenuList docs={menus} />
          ) : (
            <ExtLink href={p.menu_url}>View submitted menu</ExtLink>
          )}
        </ReviewBlock>
      ) : null}

      {specials.length > 0 ? (
        <ReviewBlock label="Specials" hidden={!shown('specials')}>
          <div className="flex flex-col gap-1">
            {specials.map((s, i) => (
              <div
                key={`sp-${i}`}
                className="flex flex-wrap items-baseline gap-2 rounded-lg border border-border bg-white/[0.03] px-3 py-1.5"
              >
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-mutedDim">
                  {summarizeDays(s?.days)}
                </span>
                <span className="min-w-0 flex-1 text-[12px] leading-relaxed text-muted">{s?.text}</span>
              </div>
            ))}
          </div>
        </ReviewBlock>
      ) : null}

      {news.length > 0 ? (
        <ReviewBlock label="Posts" hidden={!shown('news')}>
          <div className="flex flex-col gap-1">
            {news.map((n, i) => {
              const options = asArray(n?.options);
              return (
                <div key={`news-${i}`} className="rounded-lg border border-border bg-white/[0.03] px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-mutedDim">
                      {n?.date ?? 'no date'}
                    </span>
                    {n?.kind === 'poll' ? (
                      <span className="rounded-full border border-accent/35 bg-accent/10 px-2 py-0.5 text-[9px] uppercase tracking-wide text-accent">
                        poll
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 whitespace-pre-wrap text-[12px] leading-relaxed text-muted">
                    {n?.text}
                  </div>
                  {options.length > 0 ? (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {options.map((o, j) => (
                        <span
                          key={`opt-${j}`}
                          className="rounded-full border border-border bg-white/[0.04] px-2 py-0.5 text-[10px] text-mutedDim"
                        >
                          {o}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </ReviewBlock>
      ) : null}

      {photos.length > 0 ? (
        <ReviewBlock label={`Photos · ${photos.length}`} hidden={!shown('photos')}>
          <div className="flex flex-wrap gap-2">
            {photos.map((url, i) => {
              const href = safeHref(url);
              return href ? (
                <a key={`${url}-${i}`} href={href} target="_blank" rel="noreferrer">
                  <img src={href} alt="" className="h-20 w-20 rounded-lg border border-border object-cover" />
                </a>
              ) : (
                <span
                  key={`${url}-${i}`}
                  className="grid h-20 w-20 place-items-center rounded-lg border border-border bg-white/[0.03] p-1 text-center text-[9px] leading-tight text-mutedDim"
                >
                  unusable URL
                </span>
              );
            })}
          </div>
        </ReviewBlock>
      ) : null}

      {tagEntries.length > 0 ? (
        <ReviewBlock label="Tags" notInApp>
          <div className="flex flex-col gap-1.5">
            {tagEntries.map(([category, tags]) => (
              <div key={category} className="flex flex-wrap items-baseline gap-1.5">
                <span className="text-[10px] uppercase tracking-wide text-mutedDim">{category}</span>
                {asArray(tags).map((t, i) => (
                  <span
                    key={`${category}-${t}-${i}`}
                    className="rounded-full border border-border bg-white/[0.04] px-2 py-0.5 text-[11px] text-muted"
                  >
                    {t}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </ReviewBlock>
      ) : null}

      {filled.website ? (
        <ReviewBlock label="Website" hidden={!shown('website')}>
          <ExtLink href={p.website}>{p.website}</ExtLink>
        </ReviewBlock>
      ) : null}

      {p.notes ? (
        <ReviewBlock label="Private notes to you">
          <p className="whitespace-pre-wrap rounded-lg border border-border bg-white/[0.03] px-3 py-2 text-[12px] leading-relaxed text-muted">
            {p.notes}
          </p>
          <div className="mt-1 text-[10px] text-mutedDim">Never shown in the app.</div>
        </ReviewBlock>
      ) : null}
    </div>
  );
}

/** Inline email entry — the day-one contact path until the crawler exists. */
function ContactCell({ target, onSaved }: { target: Target; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(target.email ?? '');
  const [name, setName] = useState(target.contact_name ?? '');
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError, toastNode } = useToast();

  async function save() {
    const trimmed = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      showError('Enter a valid email address');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_set_outreach_contact', {
        p_venue_id: target.venue_id,
        p_email: trimmed,
        p_name: name.trim() || null,
        p_source: 'manual',
      });
      if (error) throw error;
      showSuccess(`Saved — ${target.venue_name ?? 'venue'} is ready to write`);
      setEditing(false);
      onSaved();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <>
        {toastNode}
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-left text-xs text-muted transition-colors hover:text-white"
        >
          {target.email ? (
            <span>{target.email}</span>
          ) : (
            <span className="text-accent">+ add email</span>
          )}
        </button>
      </>
    );
  }

  return (
    <>
      {toastNode}
      <div className="flex flex-wrap items-center gap-1.5">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="owner@venue.com"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="admin-input w-48 text-xs"
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name (optional)"
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="admin-input w-36 text-xs"
        />
        <button
          type="button"
          disabled={saving}
          onClick={() => void save()}
          className="admin-btn admin-btn-primary admin-btn-sm"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="admin-btn admin-btn-secondary admin-btn-sm"
        >
          Cancel
        </button>
      </div>
    </>
  );
}

/** Full email preview — what actually goes on the wire, rendered in an isolated iframe. */
function EmailPreview({ target, onClose }: { target: Target; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-surface"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="admin-label-primary truncate text-[14px]">
                {target.venue_name ?? 'Venue'}
              </div>
              <div className="mt-0.5 truncate text-[11px] text-mutedDim">
                To {target.email ?? '—'}
                {target.variant ? ` · variant ${target.variant}` : ''}
              </div>
            </div>
            <button type="button" onClick={onClose} className="admin-btn admin-btn-secondary admin-btn-sm">
              Close
            </button>
          </div>
          <div className="mt-3 rounded-lg border border-border bg-white/[0.03] px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.08em] text-mutedDim">Subject</div>
            <div className="mt-0.5 text-[13px] text-white">{target.subject ?? '—'}</div>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-[#141518]">
          {target.body ? (
            <iframe
              title="Email preview"
              // sandbox with no allow-scripts: generated HTML renders, but cannot execute.
              sandbox=""
              srcDoc={target.body}
              className="h-[560px] w-full border-0"
            />
          ) : (
            <div className="px-5 py-12 text-center text-[13px] text-mutedDim">
              Not written yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionHeading({
  step,
  title,
  hint,
  action,
}: {
  /** Pipeline is a numbered funnel, so its headings carry a step. Headings outside it do not. */
  step?: number;
  title: string;
  hint: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {step === undefined ? null : (
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full border border-border bg-white/[0.04] text-[10px] font-semibold text-muted">
              {step}
            </span>
          )}
          <span className="admin-label-primary text-[14px]">{title}</span>
        </div>
        <p className="mt-1 text-[11px] leading-relaxed text-mutedDim">{hint}</p>
      </div>
      {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
    </div>
  );
}

/** One conversation with a venue (or with someone who can't sign in yet). */
function InboxThread({ thread, onChanged }: { thread: Thread; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<ThreadMsg[]>([]);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const { showSuccess, showError, toastNode } = useToast();

  async function load() {
    const { data } = await supabase.rpc('admin_venue_thread', { p_email: thread.from_email });
    setMsgs((data as ThreadMsg[]) ?? []);
  }

  async function toggle() {
    const next = !open;
    setOpen(next);
    // Opening marks the thread read server-side, so refresh the list badge after.
    if (next) {
      await load();
      onChanged();
    }
  }

  async function send() {
    setBusy(true);
    try {
      const { error } = await supabase.rpc('admin_reply_venue_thread', {
        p_email: thread.from_email,
        p_body: reply,
      });
      if (error) throw error;
      setReply('');
      await load();
      onChanged();
      showSuccess('Reply sent');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Could not send');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`rounded-xl border p-4 ${thread.unread > 0 ? 'border-accent/45 bg-accent/[0.05]' : 'border-border bg-surface'}`}>
      {toastNode}
      <button type="button" onClick={() => void toggle()} className="flex w-full items-start justify-between gap-4 text-left">
        <span className="min-w-0">
          <span className="admin-label-primary block truncate text-[13px]">
            {thread.venue_name ?? thread.from_email}
            {thread.has_help ? (
              <span className="ml-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[9px] uppercase tracking-wide text-amber-200">
                help
              </span>
            ) : null}
          </span>
          <span className="mt-0.5 block truncate text-[11px] text-mutedDim">{thread.from_email}</span>
          <span className="mt-1 block truncate text-[12px] text-muted">{thread.last_body}</span>
        </span>
        <span className="shrink-0 text-right">
          {thread.unread > 0 ? (
            <span className="mb-1 inline-block rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-white">
              {thread.unread}
            </span>
          ) : null}
          <span className="block text-[10px] text-mutedDim">{timeAgo(thread.last_at)}</span>
        </span>
      </button>

      {open ? (
        <div className="mt-4 border-t border-border pt-4">
          <div className="mb-3 flex flex-col gap-2">
            {msgs.map((m, i) => (
              <div
                key={i}
                className={`max-w-[560px] rounded-lg px-3 py-2 text-[12px] leading-relaxed ${
                  m.direction === 'in'
                    ? 'bg-white/[0.04] text-muted'
                    : 'ml-auto bg-accent/15 text-white'
                }`}
              >
                {m.body}
                <span className="mt-1 block text-[10px] text-mutedDim">{timeAgo(m.at)}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Reply…"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && reply.trim()) void send();
              }}
              className="admin-input flex-1 text-xs"
            />
            <button
              type="button"
              disabled={busy || !reply.trim()}
              onClick={() => void send()}
              className="admin-btn admin-btn-primary admin-btn-sm"
            >
              Send
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** One venue's whole relationship, assembled in `venueRows` and rendered by the CRM row + drawer. */
type CrmRow = {
  venue_id: string;
  name: string;
  city: string | null;
  target?: Target;
  access?: PortalAccess;
  profile?: ProfileSubmission;
  claim?: Claim;
  interests: string[];
  thread?: Thread;
};

/**
 * CRM filters. Each is a QUESTION YOU ACTUALLY ASK of the list — "what needs me right now", "who has
 * gone quiet", "who wants something" — rather than a field-by-field filter builder. Anything that
 * cannot change what you do next does not earn a chip.
 */
const CRM_FILTERS: { id: string; label: string; test: (r: CrmRow) => boolean }[] = [
  /* Evan 2026-09-28: "All I want to see on this page is those that have portal access. When I
     see 'All' it shouldn't be 'All.' It should just be 'Portal Access.'" The default IS the
     access list; the other two chips are subsets/adjacent, never a wider net. */
  { id: 'access', label: 'Portal access', test: (r) => !!r.access },
  { id: 'signed_in', label: 'Logged in', test: (r) => !!r.access?.last_seen_at },
  {
    id: 'reached',
    label: 'Reached out',
    test: (r) => !!r.target && ['sent', 'opened', 'replied', 'claimed'].includes(String(r.target.status)),
  },
];

/**
 * The cold-outreach funnel, as a sequence. Counts come from the same arrays the sections render, so
 * a chip can never disagree with the section behind it.
 */
const PIPE_STEPS: {
  id: number;
  label: string;
  alarm?: boolean;
  count: (
    counts: Counts | null,
    targets: Target[],
    needsEmail: Target[],
    awaitingApproval: Target[],
    inFlight: Target[],
    replies: Reply[],
    problems: Target[],
  ) => number;
}[] = [
  { id: 1, label: 'Find', count: () => 0 },
  { id: 2, label: 'Emails', count: (_c, _t, needsEmail) => needsEmail.length },
  { id: 3, label: 'Approve', count: (_c, _t, _n, awaiting) => awaiting.length },
  { id: 4, label: 'In flight', count: (_c, _t, _n, _a, inFlight) => inFlight.length },
  { id: 5, label: 'Replies', count: (_c, _t, _n, _a, _i, replies) => replies.length },
  { id: 6, label: 'Problems', alarm: true, count: (_c, _t, _n, _a, _i, _r, problems) => problems.length },
];

/** The visibility keys the public read actually gates on — same list the server allow-lists. */
const VIS_SECTIONS: { key: string; label: string }[] = [
  { key: 'description', label: 'Description' },
  { key: 'essentials', label: 'Entry & other information' },
  { key: 'hours', label: 'Opening hours' },
  { key: 'happy_hour', label: 'Happy hour' },
  { key: 'menu', label: 'Menus' },
  { key: 'specials', label: 'Daily specials' },
  { key: 'photos', label: 'Photos' },
  { key: 'website', label: 'Website' },
  { key: 'venue_tags', label: 'Venue tags' },
  { key: 'news', label: 'Posts' },
];

/**
 * ONE VENUE, EVERYTHING ABOUT IT (Evan 2026-08-16: "when I click on a venue in the CRM tab, there
 * could be a proper popup that I can further interact with").
 *
 * WHY THE CHECKLIST IS THE POINT: approving used to be all-or-nothing — take everything the venue
 * switched on, or reject the whole submission over one bad field. The reviewer now ticks exactly
 * what goes live and approves the rest in the same click; `admin_review_venue_profile` applies the
 * visibility map in the same transaction as the status flip.
 *
 * A TICK IS A PROMISE ABOUT THE APP, so a section the venue submitted nothing for is disabled —
 * ticking "Menus" on a submission with no menus would claim something will appear that cannot.
 */
type GalleryPhoto = { url: string; source: 'user' | 'venue'; photo_id?: string };

/**
 * ⚠️ ONE ORDERED GALLERY ACROSS TWO TABLES (Evan 2026-08-17: "I should be able to choose my desired
 * order that includes both photos uploaded by users and approved, and also photos that were
 * submitted by the venue").
 *
 * User photos are rows in `venue_photos`; venue photos are a jsonb array on the submission. Neither
 * could express "this venue photo sits between those two user photos", which is why approving a
 * venue's upload used to be a blind act. This shows both sets in one list, tagged by origin, and
 * writes the order back to whichever table each item came from.
 */
export function PhotoOrderPanel({ venueId, busy, run }: {
  venueId: string;
  busy: boolean;
  run: <T,>(fn: () => Promise<T>, success: string) => void;
}) {
  const [photos, setPhotos] = useState<GalleryPhoto[] | null>(null);
  // Index of the thumb being dragged (Evan 2026-10-07: "i should be able to drag these around").
  // Reordering happens live on dragover so the row slides as you drag; Save is unchanged.
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  // Two-click removal (Evan 2026-10-07: "i should also be able to remove photos from being
  // displayed"): first ✕ arms the thumb, then Remove calls admin_remove_carousel_photo — user
  // photos get rejected=true (reversible, nothing deleted), venue photos drop out of photo_urls,
  // and if the removed photo was the venue hero the next carousel photo is promoted.
  const [removeConfirmIdx, setRemoveConfirmIdx] = useState<number | null>(null);

  const load = useCallback(async () => {
    const { data } = await supabase.rpc('admin_venue_photo_gallery', { p_venue_id: venueId });
    const g = (data ?? {}) as { user_photos?: GalleryPhoto[]; venue_photos?: GalleryPhoto[] };
    setPhotos([...(g.user_photos ?? []), ...(g.venue_photos ?? [])]);
  }, [venueId]);

  useEffect(() => { void load(); }, [load]);

  if (!photos) return <p className="text-[11px] text-mutedDim">Loading photos…</p>;
  if (photos.length === 0) {
    return <p className="text-[11px] text-mutedDim">No approved or submitted photos for this venue.</p>;
  }

  const move = (from: number, to: number) => {
    if (to < 0 || to >= photos.length) return;
    const next = photos.slice();
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setPhotos(next);
  };

  return (
    <div>
      <p className="mb-2 text-[11px] leading-relaxed text-mutedDim">
        The order the app shows them in, across both sources. First is the hero. Drag to reorder (or use ‹ ›). ✕ removes a photo from the app (user photos are kept, just hidden — undoable later).
      </p>
      <div className="flex flex-wrap gap-2">
        {photos.map((ph, i) => (
          <div
            key={`${ph.url}#${i}`}
            draggable
            onDragStart={(e) => {
              // Some browsers need data set for the drag to start; the payload itself is unused.
              e.dataTransfer.setData('text/plain', String(i));
              e.dataTransfer.effectAllowed = 'move';
              setDragIdx(i);
              setRemoveConfirmIdx(null);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (dragIdx !== null && dragIdx !== i) {
                move(dragIdx, i);
                setDragIdx(i);
              }
            }}
            onDrop={(e) => e.preventDefault()}
            onDragEnd={() => setDragIdx(null)}
            className={`relative h-24 w-24 cursor-grab overflow-hidden rounded-lg border active:cursor-grabbing ${
              dragIdx === i ? 'border-accent opacity-60' : 'border-border'
            }`}
          >
            <img src={ph.url} alt="" draggable={false} className="h-full w-full object-cover" />
            <span className="absolute left-1 top-1 rounded bg-black/75 px-1.5 text-[10px] font-semibold text-white">
              {i + 1}
            </span>
            <span
              className={`absolute right-1 top-1 rounded px-1.5 text-[9px] font-semibold uppercase ${
                ph.source === 'venue'
                  ? 'bg-accent/85 text-white'
                  : 'bg-black/75 text-mutedDim'
              }`}
              title={ph.source === 'venue' ? 'Submitted by the venue' : 'Shot by a user, already approved'}
            >
              {ph.source}
            </span>
            {removeConfirmIdx === i ? (
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-black/85 py-0.5">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setRemoveConfirmIdx(null);
                    run(async () => {
                      const { error } = await supabase.rpc('admin_remove_carousel_photo', {
                        p_venue_id: venueId,
                        p_url: ph.url,
                      });
                      if (error) {
                        if (/admin_remove_carousel_photo|42883|schema cache/i.test(error.message)) {
                          throw new Error(
                            'Removal needs SQL applied first: supabase/sql/admin_remove_carousel_photo_v1.sql',
                          );
                        }
                        throw error;
                      }
                      await load();
                    }, 'Photo removed from the carousel');
                  }}
                  className="px-1 text-[11px] font-semibold text-red-400 disabled:opacity-30"
                >
                  Remove
                </button>
                <button
                  type="button"
                  onClick={() => setRemoveConfirmIdx(null)}
                  className="px-1 text-[11px] text-mutedDim hover:text-white"
                >
                  Keep
                </button>
              </div>
            ) : (
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-black/70 py-0.5">
                <button type="button" disabled={i === 0} onClick={() => move(i, i - 1)} className="px-1 text-[11px] text-white disabled:opacity-30">‹</button>
                <button type="button" disabled={i === photos.length - 1} onClick={() => move(i, i + 1)} className="px-1 text-[11px] text-white disabled:opacity-30">›</button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setRemoveConfirmIdx(i)}
                  title="Remove this photo from the carousel"
                  className="px-1 text-[11px] text-red-400/80 hover:text-red-300 disabled:opacity-30"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            run(async () => {
              const { error } = await supabase.rpc('admin_set_venue_photo_order', {
                p_venue_id: venueId,
                p_urls: photos.map((p2) => p2.url),
              });
              if (error) throw error;
              await load();
            }, 'Photo order saved')
          }
          className="admin-btn admin-btn-secondary admin-btn-sm"
        >
          Save photo order
        </button>
        <button type="button" disabled={busy} onClick={() => void load()} className="text-[11px] text-mutedDim hover:text-white">
          Reset
        </button>
      </div>
    </div>
  );
}

/**
 * Per-venue Promotions switch (Evan 2026-10-09: "i should be able to turn on promotions for a
 * venue on the venue portal tab"). ON makes the portal's gold Promotions section appear even
 * with zero offers, so the venue can propose their own; a venue that already has offer rows
 * sees the section regardless (the old first-offer-is-the-switch behavior still stands).
 * Reads/writes venue_portal_settings directly — admin-only RLS, same pattern as
 * admin_dash_settings; the portal itself reads the flag only through
 * venue_portal_promotions_enabled().
 */
function PromotionsFlagToggle({
  venueId,
  busy,
  run,
}: {
  venueId: string;
  busy: boolean;
  run: <T,>(fn: () => Promise<T>, success: string) => void;
}) {
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('venue_portal_settings')
        .select('promotions_enabled')
        .eq('venue_id', venueId)
        .maybeSingle();
      if (!cancelled) setEnabled(error ? null : Boolean(data?.promotions_enabled));
    })();
    return () => {
      cancelled = true;
    };
  }, [venueId]);

  return (
    <div>
      <div className="mb-2 text-[10px] uppercase tracking-[0.08em] text-mutedDim">Promotions</div>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-white/[0.02] p-3">
        <p className="min-w-0 flex-1 text-[11px] leading-relaxed text-mutedDim">
          On = the gold Promotions section shows in their portal even before any offer exists, so
          they can propose deals to you. A venue with offers already sees it either way.
        </p>
        <button
          type="button"
          disabled={busy || enabled === null}
          onClick={() => {
            const next = !enabled;
            setEnabled(next);
            run(async () => {
              const { error } = await supabase
                .from('venue_portal_settings')
                .upsert({ venue_id: venueId, promotions_enabled: next, updated_at: new Date().toISOString() })
                .select('venue_id');
              if (error) {
                setEnabled(!next);
                throw error;
              }
            }, next ? 'Promotions turned on for this venue' : 'Promotions turned off for this venue');
          }}
          className={`shrink-0 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ${
            enabled
              ? 'border-amber-400/50 bg-amber-400/10 text-amber-300 hover:bg-amber-400/20'
              : 'border-border text-mutedDim hover:text-white'
          }`}
        >
          {enabled === null ? '…' : enabled ? 'Promotions on' : 'Promotions off'}
        </button>
      </div>
    </div>
  );
}

function VenueDrawer({
  row,
  accessRows,
  busy,
  onClose,
  onReview,
  onGrant,
  onRevoke,
  onTogglePost,
  onClearProfile,
  run,
  onChanged,
}: {
  row: CrmRow;
  /**
   * EVERY access row for this venue, not just the one `venueRows` folded onto `row.access`
   * (Evan 2026-08-16: "when I click on a venue in CRM I need to be able to change / add / edit
   * access for different email addresses"). A venue can have an owner and a manager; the CRM row
   * only ever kept the last one it saw.
   */
  accessRows: PortalAccess[];
  busy: boolean;
  onClose: () => void;
  onReview: (venueId: string, approve: boolean, note: string | null, vis: Record<string, boolean> | null) => void;
  onGrant: (venueId: string, email: string) => void;
  onRevoke: (a: PortalAccess) => void;
  onTogglePost: (a: PortalAccess) => void;
  onClearProfile: (venueId: string, venueName: string) => void;
  run: <T,>(fn: () => Promise<T>, success: string) => void;
  onChanged: () => void;
}) {
  const p = row.profile;
  const pending = p?.status === 'pending';
  const [newEmail, setNewEmail] = useState('');
  const [inviteOnGrant, setInviteOnGrant] = useState(true);

  const [vis, setVis] = useState<Record<string, boolean>>(() => {
    const base: Record<string, boolean> = {};
    VIS_SECTIONS.forEach((sec) => {
      base[sec.key] = (p?.visibility ?? {})[sec.key] === true;
    });
    return base;
  });

  const hasContent = (key: string): boolean => {
    if (!p) return false;
    switch (key) {
      case 'description': return Boolean((p.description ?? '').trim());
      case 'essentials': return Object.keys(p.essentials ?? {}).length > 0;
      case 'hours': return asArray(p.hours).length > 0;
      case 'happy_hour': return asArray(p.happy_hour).length > 0;
      case 'menu': return asArray(p.menus).length > 0 || Boolean(p.menu_url);
      case 'specials': return asArray(p.specials).length > 0;
      case 'photos': return asArray(p.photo_urls).length > 0;
      case 'website': return Boolean((p.website ?? '').trim());
      case 'venue_tags': return Object.values(p.venue_tags ?? {}).some((t) => asArray(t).length > 0);
      case 'news': return asArray(p.news).length > 0;
      default: return false;
    }
  };

  const live = VIS_SECTIONS.filter((sec) => vis[sec.key] && hasContent(sec.key));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-8">
      {/* Scrim is a SIBLING of the panel, never its parent — a click-catcher wrapped around a
          scrollable panel eats the scroll. */}
      <button type="button" aria-label="Close" onClick={onClose} className="fixed inset-0 cursor-default" />
      <div className="relative w-full max-w-3xl rounded-2xl border border-border bg-surface shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <div className="admin-label-primary truncate text-[16px]">{row.name}</div>
            <div className="mt-0.5 truncate text-[11px] text-mutedDim">
              {row.city ?? '—'}
              {row.access ? ` · ${row.access.email}` : ' · no portal access'}
              {row.access?.last_seen_at ? ` · seen ${timeAgo(row.access.last_seen_at)}` : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-[12px] text-mutedDim transition-colors hover:text-white"
          >
            Close
          </button>
        </div>

        <div className="max-h-[75vh] space-y-6 overflow-y-auto px-5 py-5">
          <div>
            <div className="mb-2 text-[10px] uppercase tracking-[0.08em] text-mutedDim">
              Portal access
            </div>
            <div className="rounded-xl border border-border bg-white/[0.02] p-3">
              {accessRows.length === 0 ? (
                <p className="mb-3 text-[11px] text-mutedDim">
                  Nobody can sign in as this venue yet.
                </p>
              ) : (
                <div className="mb-3 divide-y divide-border/60">
                  {accessRows.map((a) => (
                    <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                      <div className="min-w-0">
                        <div className="truncate text-[12px] text-white">{a.email}</div>
                        <div className="truncate text-[10px] text-mutedDim">
                          {a.revoked_at
                            ? `revoked ${timeAgo(a.revoked_at)}`
                            : a.last_seen_at
                              ? `seen ${timeAgo(a.last_seen_at)}`
                              : 'never signed in'}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {/*
                          Posting is a SEPARATE control from access (Evan 2026-08-16). A venue can be
                          able to sign in and edit without being able to post to the feed.
                        */}
                        {/*
                          Re-send for someone who lost the email (Evan 2026-08-17). Only on a LIVE
                          row — the function refuses a revoked address anyway, so offering it there
                          would be a button that can only fail.
                        */}
                        {a.revoked_at ? null : (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              run(async () => {
                                await sendVenueInvite(a.venue_id, a.email);
                              }, `Invite re-sent to ${a.email}`)
                            }
                            className="text-[11px] text-mutedDim transition-colors hover:text-white"
                          >
                            Resend invite
                          </button>
                        )}
                        {a.revoked_at ? null : (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onTogglePost(a)}
                            className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                              a.can_post
                                ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-200'
                                : 'border-border bg-white/[0.04] text-mutedDim hover:text-white'
                            }`}
                            title="Can this venue post to the feed?"
                          >
                            {a.can_post ? 'Posting on' : 'Posting off'}
                          </button>
                        )}
                        {a.revoked_at ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onGrant(a.venue_id, a.email)}
                            className="text-[11px] text-accent transition-colors hover:text-white"
                          >
                            Restore
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onRevoke(a)}
                            className="text-[11px] text-mutedDim transition-colors hover:text-red-300"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="add another email…"
                  className="admin-input min-w-[200px] flex-1 text-xs"
                />
                <label className="flex shrink-0 cursor-pointer items-center gap-2 text-[11px] text-muted">
                  <input
                    type="checkbox"
                    checked={inviteOnGrant}
                    onChange={(e) => setInviteOnGrant(e.target.checked)}
                  />
                  Email invite
                </label>
                <button
                  type="button"
                  disabled={busy || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail.trim())}
                  onClick={() => {
                    const addr = newEmail.trim().toLowerCase();
                    setNewEmail('');
                    run(async () => {
                      const { error } = await supabase.rpc('admin_grant_venue_portal_access', {
                        p_venue_id: row.venue_id,
                        p_email: addr,
                      });
                      if (error) throw error;
                      if (inviteOnGrant) await sendVenueInvite(row.venue_id, addr);
                    }, inviteOnGrant ? `Access granted and invite emailed to ${addr}` : `Access granted to ${addr}`);
                  }}
                  className="admin-btn admin-btn-secondary admin-btn-sm shrink-0"
                >
                  Grant
                </button>
              </div>
              <p className="mt-2 text-[10px] leading-relaxed text-mutedDim">
                They sign in at portal.joinshado.com with a 6-digit code. Granting sends nothing —
                tell them yourself.
              </p>
            </div>
          </div>

          <PromotionsFlagToggle venueId={row.venue_id} busy={busy} run={run} />

          {row.interests.length ? (
            <div>
              <div className="mb-2 text-[10px] uppercase tracking-[0.08em] text-mutedDim">Wants</div>
              <div className="flex flex-wrap gap-1">
                {row.interests.map((t) => (
                  <span key={t} className="rounded-full border border-accent/35 bg-accent/10 px-2 py-0.5 text-[10px] text-accent">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {p?.proposed_name ? (
            <div>
              <div className="mb-2 text-[10px] uppercase tracking-[0.08em] text-mutedDim">
                Name change requested
              </div>
              {/*
                ⚠️ SEPARATE FROM APPROVE (Evan 2026-08-17: "with my approval only"). Approving the
                submission publishes their COPY; it does not rename the venue. Renaming is visible to
                every user of the app instantly and is not obviously wrong to a later reviewer, so it
                gets its own deliberate button.
              */}
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/[0.06] p-3">
                <div className="min-w-0 text-[12px]">
                  <span className="text-mutedDim">{row.name}</span>
                  <span className="mx-2 text-mutedDim">→</span>
                  <span className="text-white">{p.proposed_name}</span>
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      const { error } = await supabase.rpc('admin_apply_venue_proposed_name', {
                        p_venue_id: p.venue_id,
                      });
                      if (error) throw error;
                    }, `Renamed to ${p.proposed_name}`)
                  }
                  className="admin-btn admin-btn-primary admin-btn-sm shrink-0"
                >
                  Apply name
                </button>
              </div>
            </div>
          ) : null}

          {p && (p.proposed_lat != null || p.proposed_address) ? (
            <div>
              <div className="mb-2 text-[10px] uppercase tracking-[0.08em] text-mutedDim">
                Location change requested
              </div>
              {/*
                Same contract as the name: approving the submission publishes copy, it does NOT move
                the pin or store the address — each gets its own deliberate Apply. Coordinates write
                venues.latitude/longitude (what get_map_venues reads, so the app pin moves);
                the address writes venues.portal_address, which nothing reads yet.
              */}
              <div className="flex flex-col gap-2 rounded-xl border border-amber-500/40 bg-amber-500/[0.06] p-3">
                {p.proposed_lat != null && p.proposed_lng != null ? (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0 text-[12px]">
                      <span className="text-mutedDim">
                        {p.current_lat != null && p.current_lng != null
                          ? `${p.current_lat.toFixed(5)}, ${p.current_lng.toFixed(5)}`
                          : 'no current pin'}
                      </span>
                      <span className="mx-2 text-mutedDim">→</span>
                      <span className="text-white">
                        {p.proposed_lat.toFixed(5)}, {p.proposed_lng.toFixed(5)}
                      </span>
                      {p.current_lat != null && p.current_lng != null ? (
                        <span className="ml-2 text-mutedDim">
                          (moves the pin {formatPinDistance(p.current_lat, p.current_lng, p.proposed_lat, p.proposed_lng)})
                        </span>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <a
                        href={`https://www.google.com/maps?q=${p.proposed_lat},${p.proposed_lng}`}
                        target="_blank"
                        rel="noreferrer"
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                      >
                        Check on map ↗
                      </a>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          run(async () => {
                            const { error } = await supabase.rpc('admin_apply_venue_proposed_coords', {
                              p_venue_id: p.venue_id,
                            });
                            if (error) throw error;
                          }, 'Pin moved to the proposed coordinates')
                        }
                        className="admin-btn admin-btn-primary admin-btn-sm"
                      >
                        Apply coordinates
                      </button>
                    </div>
                  </div>
                ) : null}
                {p.proposed_address ? (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0 text-[12px] text-white">{p.proposed_address}</div>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        run(async () => {
                          const { error } = await supabase.rpc('admin_apply_venue_proposed_address', {
                            p_venue_id: p.venue_id,
                          });
                          if (error) throw error;
                        }, 'Address stored on the venue')
                      }
                      className="admin-btn admin-btn-primary admin-btn-sm shrink-0"
                    >
                      Apply address
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {p ? (
            <div>
              <div className="mb-2 text-[10px] uppercase tracking-[0.08em] text-mutedDim">
                What goes live on the venue page
              </div>
              <div className="grid grid-cols-1 gap-1.5 rounded-xl border border-border bg-white/[0.02] p-3 sm:grid-cols-2">
                {VIS_SECTIONS.map((sec) => {
                  const empty = !hasContent(sec.key);
                  return (
                    <label
                      key={sec.key}
                      className={`flex items-center gap-2 text-[12px] ${
                        empty ? 'cursor-not-allowed text-mutedDim/60' : 'cursor-pointer text-muted'
                      }`}
                    >
                      <input
                        type="checkbox"
                        disabled={empty || busy}
                        checked={Boolean(vis[sec.key]) && !empty}
                        onChange={(e) => setVis((prev) => ({ ...prev, [sec.key]: e.target.checked }))}
                      />
                      <span className="truncate">{sec.label}</span>
                      {empty ? <span className="text-[10px]">(nothing submitted)</span> : null}
                    </label>
                  );
                })}
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-mutedDim">
                {pending || p.status === 'approved' ? (
                  live.length ? (
                    <>
                      {p.status === 'approved' ? 'Live now' : 'Approving publishes'}{' '}
                      <span className="text-muted">{live.map((l) => l.label).join(', ')}</span>. Everything
                      else is hidden.
                    </>
                  ) : (
                    <>Nothing is ticked, so approving publishes <span className="text-muted">nothing</span>.</>
                  )
                ) : (
                  <>
                    This submission is <span className="text-muted">{p.status}</span> — the venue must edit it
                    before it can be reviewed again.
                  </>
                )}
              </p>
            </div>
          ) : (
            <EmptyState
              compact
              title="No content submitted"
              hint="This venue has not filled in its portal profile yet."
            />
          )}

          <div>
            <div className="mb-2 text-[10px] uppercase tracking-[0.08em] text-mutedDim">
              Photos — order shown in the app
            </div>
            <div className="rounded-xl border border-border bg-white/[0.02] p-3">
              <PhotoOrderPanel venueId={row.venue_id} busy={busy} run={run} />
            </div>
          </div>

          {p ? (
            <ProfileReviewCard
              p={p}
              busy={busy}
              visOverride={vis}
              hideActions
              onApprove={() => {}}
              onReject={() => {}}
            />
          ) : null}

          {row.thread ? (
            <div>
              <div className="mb-2 text-[10px] uppercase tracking-[0.08em] text-mutedDim">Messages</div>
              <InboxThread thread={row.thread} onChanged={onChanged} />
            </div>
          ) : null}
        </div>

        {p ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-3">
            {/*
              ⚠️ DESTRUCTIVE, AND UNLIKE THE PORTAL'S Clear all THIS ONE IS IMMEDIATE. The portal's
              only empties a form the venue has not saved; this deletes the stored submission, so it
              confirms first. Available whatever the status — the usual reason to reach for it is a
              row that is already approved and wrong.
            */}
            <button
              type="button"
              disabled={busy}
              onClick={() => onClearProfile(p.venue_id, row.name)}
              className="mr-auto text-[11px] text-mutedDim transition-colors hover:text-red-300"
            >
              Clear all content
            </button>
            {pending ? (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const note = window.prompt('Why is this being rejected? (shown to the venue)');
                    if (note === null) return;
                    onReview(p.venue_id, false, note || null, null);
                  }}
                  className="text-[11px] text-mutedDim transition-colors hover:text-red-300"
                >
                  Reject
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onReview(p.venue_id, true, null, vis)}
                  className="admin-btn admin-btn-primary admin-btn-sm"
                >
                  Approve{live.length ? ` · ${live.length} section${live.length === 1 ? '' : 's'}` : ' (nothing visible)'}
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const PORTAL_ORIGIN = 'https://portal.joinshado.com';
/** Must equal `NS` in `venue-portal/src/adminHandoff.ts`. */
const PORTAL_HANDOFF_NS = 'shado-portal-handoff';
/** Outer bound. The portal gives up at 12s, so in practice it closes the handshake first. */
const PORTAL_HANDOFF_TIMEOUT_MS = 15_000;

/**
 * OPEN A VENUE'S PORTAL AS EVAN, LANDED ON THAT VENUE.
 *
 * Evan, 2026-09-28: *"when i click get access … i want to be routed straight into the venue portal
 * for that venue. bang. that easy. then i see exactly what they see and can take action on that end
 * as if it was on their behalf."*
 *
 * Two separate problems, solved separately — conflating them is how the first attempt shipped a
 * working grant that landed on a login screen:
 *
 *   1. **Authorisation** is `admin_open_venue_portal` (the caller's job, not this function's). It
 *      writes a real `venue_portal_access` row for Evan's own `auth.uid()`. ⚠️ Nothing here — and
 *      nothing in the portal — adds a branch to `_venue_portal_can_edit`. Its one rule is still
 *      `a.user_id = auth.uid()`; Evan passes it because he genuinely holds the row. The email
 *      comparison removed on 2026-09-27 stays removed.
 *   2. **Authentication** is this function. `portal.joinshado.com` is a different origin with its
 *      own `sessionStorage`, so it starts out knowing nothing about the dash — which is exactly
 *      what Evan saw. We hand it this tab's Supabase session over `postMessage`.
 *
 * ⚠️ WHY NOT `#access_token=…&refresh_token=…`, WHICH WOULD NEED NO PORTAL CODE AT ALL (the portal
 * already sets `detectSessionInUrl: true`)? Because the credential is a PLATFORM-ADMIN credential —
 * `profiles.is_admin`, the single input `_assert_admin_caller()` trusts across ~60 `admin_*` RPCs —
 * and browsers write the full URL, fragment included, into the on-disk history database. That would
 * persist a long-lived, rotating refresh token for the admin account in a plaintext file readable by
 * any extension with the `history` permission. A fragment never reaching a server does not answer
 * that. `postMessage` writes it nowhere.
 *
 * ⚠️ `window.open` WITHOUT `noopener`, DELIBERATELY. `window.opener` IS the channel; `noopener`
 * severs it. The accepted cost is that the portal tab can navigate this one — reverse tabnabbing
 * between two origins we both own and neither of which navigates its opener.
 *
 * The protocol tolerates either ordering, because there is no ordering to rely on: the portal polls
 * its opener until answered, and we answer once the grant has returned.
 */
function openVenuePortal(venueId: string) {
  const nonce =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  /**
   * ⚠️ Neither param is a secret. `v` is a venue id, which authorises nothing — the portal resolves
   * it against what `venue_portal_my_venues()` returns and ignores it otherwise. `h` is a nonce that
   * is only ever compared against the value held in this closure.
   */
  const win = window.open(
    `${PORTAL_ORIGIN}/?v=${encodeURIComponent(venueId)}&h=${encodeURIComponent(nonce)}`,
    '_blank',
  );

  let onReady: (() => void) | null = null;
  const ready = new Promise<void>((resolve) => {
    onReady = resolve;
  });

  const onMessage = (e: MessageEvent) => {
    // Three checks, all required: who sent it, which window sent it, and whether it is ours.
    if (e.origin !== PORTAL_ORIGIN) return;
    if (!win || e.source !== win) return;
    const d = e.data as { ns?: unknown; type?: unknown; nonce?: unknown } | null;
    if (!d || d.ns !== PORTAL_HANDOFF_NS || d.type !== 'ready' || d.nonce !== nonce) return;
    onReady?.();
  };
  window.addEventListener('message', onMessage);

  let done = false;
  const cleanup = () => {
    if (done) return;
    done = true;
    window.removeEventListener('message', onMessage);
  };
  // Belt: a listener must never outlive the click that made it, whatever happens above.
  const sweeper = window.setTimeout(cleanup, PORTAL_HANDOFF_TIMEOUT_MS + 1_000);

  return {
    /** The grant failed — there is nothing to hand over, so stop listening. */
    abort() {
      window.clearTimeout(sweeper);
      cleanup();
    },
    /**
     * Call once `admin_open_venue_portal` has returned. Throws a sentence Evan can act on, which
     * `run()` surfaces as an error toast.
     */
    async handOffSession() {
      try {
        if (!win) {
          throw new Error('The portal tab was blocked — allow pop-ups for this site and try again.');
        }
        let timer: number | undefined;
        await Promise.race([
          ready,
          new Promise<never>((_, reject) => {
            timer = window.setTimeout(
              () =>
                reject(
                  new Error(
                    'The portal tab did not answer. Open portal.joinshado.com yourself — the access grant did go through.',
                  ),
                ),
              PORTAL_HANDOFF_TIMEOUT_MS,
            );
          }),
        ]).finally(() => {
          if (timer !== undefined) window.clearTimeout(timer);
        });

        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        const session = data.session;
        if (!session) throw new Error('Your admin session has expired — sign in again.');

        // ⚠️ Addressed to PORTAL_ORIGIN, never '*'. A wildcard would hand the session to whatever
        // that tab had been navigated to.
        win.postMessage(
          {
            ns: PORTAL_HANDOFF_NS,
            type: 'session',
            nonce,
            access_token: session.access_token,
            refresh_token: session.refresh_token,
          },
          PORTAL_ORIGIN,
        );
      } finally {
        window.clearTimeout(sweeper);
        cleanup();
      }
    },
  };
}

export function VenueOutreachScreen() {
  const [targets, setTargets] = useState<Target[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [profiles, setProfiles] = useState<ProfileSubmission[]>([]);
  const [interest, setInterest] = useState<VenueInterest[]>([]);
  const [access, setAccess] = useState<PortalAccess[]>([]);
  const [threads, setThreads] = useState<Thread[]>([]);
  /**
   * The people-first cut of the same relationships — see `VenuePeopleModal`. Flat (person, venue,
   * attachment) rows; the modal folds them. Kept in `load()` rather than fetched on open so the
   * button can carry a count, and because a stale count is worse than one extra tiny RPC.
   */
  const [people, setPeople] = useState<VenuePersonRow[]>([]);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [view, setView] = useState<'pipeline' | 'crm' | 'promos'>('crm');
  /** The venue whose drawer is open, by id — never the object, so a reload re-reads fresh data. */
  const [openVenueId, setOpenVenueId] = useState<string | null>(null);
  /** Which funnel step Pipeline is showing — see `PIPE_STEPS`. */
  const [pipeStep, setPipeStep] = useState(1);
  const [signups, setSignups] = useState<VenueSignup[]>([]);
  /** The access row awaiting a revoke confirmation — see the note at the Revoke button. */
  const [confirmRevoke, setConfirmRevoke] = useState<PortalAccess | null>(null);

  /** CRM filter — see `CRM_FILTERS`. */
  const [crmFilter, setCrmFilter] = useState<string>('access');
  const [crmQuery, setCrmQuery] = useState('');
  const [showInterest, setShowInterest] = useState(false);
  const [manageAccess, setManageAccess] = useState(false);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [sending, setSending] = useState<SendingState | null>(null);
  const [cities, setCities] = useState<CityOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [discoverScope, setDiscoverScope] = useState<'launch' | 'all_us'>('launch');
  const [discoverLimit, setDiscoverLimit] = useState(25);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<Target | null>(null);
  const [confirmApprove, setConfirmApprove] = useState<Batch | null>(null);
  const [confirmSending, setConfirmSending] = useState<boolean | null>(null);
  const [preflight, setPreflight] = useState<Preflight | null>(null);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const { showSuccess, showError, toastNode } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [targetsRes, batchesRes, countsRes, stateRes, repliesRes, claimsRes, profilesRes, interestRes, accessRes, threadsRes, signupsRes, citiesRes, peopleRes] =
        await Promise.all([
          supabase.rpc('admin_list_outreach_targets', { p_status: null, p_limit: 500 }),
          supabase.rpc('admin_list_outreach_batches', { p_limit: 50 }),
          supabase.rpc('admin_outreach_pipeline_counts'),
          supabase.rpc('admin_outreach_sending_state'),
          supabase.rpc('admin_list_outreach_replies', { p_limit: 50 }),
          supabase.rpc('admin_list_venue_claims', { p_status: 'pending', p_limit: 100 }),
          supabase.rpc('admin_list_venue_profile_submissions', { p_status: 'pending', p_limit: 100 }),
          supabase.rpc('admin_list_venue_interest', { p_limit: 200 }),
          supabase.rpc('admin_list_venue_portal_access', { p_limit: 200 }),
          supabase.rpc('admin_list_venue_threads', { p_limit: 200 }),
          supabase.rpc('admin_list_venue_signups', { p_limit: 100 }),
          supabase.from('cities').select('id, name').in('name', LAUNCH_CITY_NAMES),
          supabase.rpc('admin_list_venue_people', { p_limit: 500 }),
        ]);
      if (targetsRes.error) throw targetsRes.error;
      if (batchesRes.error) throw batchesRes.error;
      if (countsRes.error) throw countsRes.error;

      setTargets((targetsRes.data as Target[]) ?? []);
      setBatches((batchesRes.data as Batch[]) ?? []);
      setCounts((countsRes.data as Counts) ?? null);
      setSending((stateRes.data as SendingState) ?? null);
      setReplies((repliesRes.data as Reply[]) ?? []);
      setClaims((claimsRes.data as Claim[]) ?? []);
      setProfiles((profilesRes.data as ProfileSubmission[]) ?? []);
      setInterest((interestRes.data as VenueInterest[]) ?? []);
      setAccess((accessRes.data as PortalAccess[]) ?? []);
      setThreads((threadsRes.data as Thread[]) ?? []);
      setSignups((signupsRes.data as VenueSignup[]) ?? []);
      setCities((citiesRes.data as CityOption[]) ?? []);
      /* Not thrown on, matching every other secondary list here: an RPC that has not been applied
         yet must not take the whole CRM down — the People button just reads zero. */
      setPeople((peopleRes.data as VenuePersonRow[]) ?? []);
      setSelected(new Set());
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load outreach pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Ask the sender whether it could actually send, without sending.
   *
   * ⚠️ THE GATES THAT BLOCK OUTREACH ARE NOT ALL IN THIS DATABASE. `admin_outreach_sending_state`
   * knows about the kill switch, the cap and the bounce guard because those are rows; it cannot know
   * that the from-address sits on a subdomain with no DKIM, or that the postal address CAN-SPAM
   * requires is still an empty string. Both were true on 2026-08-18 and the banner said only
   * "Sending is off" — the real reasons were two layers down. This button surfaces them.
   *
   * Deliberately NOT folded into `load()`: it is an outbound call to the ESP on every page open,
   * and the answer only changes when config or DNS does.
   */
  async function runPreflight() {
    setPreflightBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('outreach-send', {
        body: { preflight: true },
      });
      if (error) throw error;
      setPreflight(data as Preflight);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Preflight failed');
    } finally {
      setPreflightBusy(false);
    }
  }

  async function run<T>(fn: () => Promise<T>, success: string) {
    setBusy(true);
    try {
      await fn();
      showSuccess(success);
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  async function discover() {
    setDiscovering(true);
    try {
      const cityIds = discoverScope === 'launch' ? cities.map((c) => c.id) : null;
      if (discoverScope === 'launch' && (!cityIds || cityIds.length === 0)) {
        throw new Error('Launch cities not resolved — hit Refresh first');
      }
      const { data, error } = await supabase.rpc('admin_discover_outreach_targets', {
        p_city_ids: cityIds,
        p_limit: discoverLimit,
      });
      if (error) throw error;
      const n = (data as number) ?? 0;
      showSuccess(
        n === 0
          ? 'No new targets — every eligible venue in scope is already in the pipeline'
          : `Added ${n} venue${n === 1 ? '' : 's'}`,
      );
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Discovery failed');
    } finally {
      setDiscovering(false);
    }
  }

  const needsEmail = useMemo(() => targets.filter((t) => t.status === 'discovered'), [targets]);
  const readyToWrite = useMemo(() => targets.filter((t) => t.status === 'contact_found'), [targets]);
  const awaitingApproval = useMemo(() => targets.filter((t) => t.status === 'queued'), [targets]);
  const inFlight = useMemo(
    () => targets.filter((t) => ['approved', 'sent', 'opened'].includes(t.status)),
    [targets],
  );
  const problems = useMemo(
    () => targets.filter((t) => t.status === 'failed' || t.bounced || t.unsubscribed),
    [targets],
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(Array.from(prev));
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /**
   * One row per venue, joined from what's already loaded — no extra round trip. This is the CRM
   * view: everything about a venue in one place instead of scattered across six tables.
   */
  const venueRows = useMemo(() => {
    const byId = new Map<string, CrmRow>();
    const put = (id: string, name: string | null, city: string | null) => {
      if (!byId.has(id)) byId.set(id, { venue_id: id, name: name ?? '—', city, interests: [] });
      return byId.get(id)!;
    };
    targets.forEach((t) => { put(t.venue_id, t.venue_name, t.city_name).target = t; });
    access.filter((a) => !a.revoked_at).forEach((a) => { put(a.venue_id, a.venue_name, a.city_name).access = a; });
    profiles.forEach((p) => { put(p.venue_id, p.venue_name, p.city_name).profile = p; });
    interest.forEach((i) => { put(i.venue_id, i.venue_name, i.city_name).interests.push(i.topic); });
    threads.filter((t) => t.venue_id).forEach((t) => { put(t.venue_id!, t.venue_name, null).thread = t; });
    /**
     * ⚠️ CRM = VENUES WITH A RELATIONSHIP, NOT THE PROSPECT LIST (Evan 2026-08-16: "none of these
     * places should show up under venues... until they've actually made an account in the venue
     * portal. I want that page to be called CRM").
     *
     * `targets` is the cold-outreach prospect list — 28 venues that have never signed in. Those
     * belong to Pipeline, whose whole job is getting them onto the platform. A venue earns a CRM row
     * by having portal ACCESS, a submitted PROFILE, a recorded partnership INTEREST, or a message
     * THREAD — i.e. anything that happens AFTER they arrive.
     *
     * ⚠️ THIS TAB WILL LOOK EMPTY AT FIRST, and that is correct rather than broken: today only The
     * Boot and The Test Room qualify.
     */
    return Array.from(byId.values())
      .filter((r) => !!r.access || !!r.profile || r.interests.length > 0 || !!r.thread)
      .sort((a, b) => {
      // Anything needing Evan first: unread messages, then content to review, then the rest.
      const score = (r: typeof a) =>
        (r.thread?.unread ? 3 : 0) + (r.profile ? 2 : 0) + (r.access ? 1 : 0);
        return score(b) - score(a) || a.name.localeCompare(b.name);
      });
  }, [targets, access, profiles, interest, threads]);

  const unreadTotal = threads.reduce((n, t) => n + t.unread, 0);
  /**
   * The People button's count: distinct `person_key`s, excluding Shado's own "Get access" grants.
   * ⚠️ It must use the SAME default as the modal (staff hidden) or the button promises a number the
   * list then contradicts.
   */
  const peopleCount = useMemo(
    () => new Set(people.filter((p) => !p.is_staff).map((p) => p.person_key)).size,
    [people],
  );
  /** Threads we cannot attach to a CRM row — see the note at the render site. */
  const orphanThreads = useMemo(() => threads.filter((t) => !t.venue_id), [threads]);
  const [clearingStep, setClearingStep] = useState(false);
  /** Removes every target in the list from the funnel via the existing supersede primitive. */
  const clearStep = useCallback(
    async (list: Target[]) => {
      if (!window.confirm(`Remove ${list.length} venue${list.length === 1 ? '' : 's'} from the outreach funnel? They go back to the untouched pool and can be rediscovered later.`)) return;
      setClearingStep(true);
      try {
        for (const t of list) {
          await supabase.rpc('admin_supersede_outreach_target', { p_target_id: t.id, p_note: 'bulk cleared from funnel' });
        }
        await load();
      } finally {
        setClearingStep(false);
      }
    },
    [load],
  );

  const crmRows = useMemo(() => {
    const f = CRM_FILTERS.find((x) => x.id === crmFilter) ?? CRM_FILTERS[0];
    const q = crmQuery.trim().toLowerCase();
    return venueRows.filter(
      (r) =>
        f.test(r) &&
        (!q ||
          r.name.toLowerCase().includes(q) ||
          (r.city ?? '').toLowerCase().includes(q) ||
          (r.access?.email ?? '').toLowerCase().includes(q)),
    );
  }, [venueRows, crmFilter, crmQuery]);

  const openVenue = useMemo(
    () => venueRows.find((r) => r.venue_id === openVenueId) ?? null,
    [venueRows, openVenueId],
  );

  /**
   * ⚠️ ONE REVIEW PATH. `p_visibility` carries the reviewer's ticks so the sections they chose and
   * the approval land in the SAME transaction — approving first and fixing visibility afterwards
   * would put content in front of users, however briefly, that the reviewer had decided to hide.
   */
  /**
   * ⚠️ ONE REVIEW PATH. `p_visibility` carries the reviewer's ticks so the sections they chose and
   * the approval land in the SAME transaction — approving first and fixing visibility afterwards
   * would put content in front of users, however briefly, that the reviewer had decided to hide.
   *
   * A plain function, not a useCallback: it closes over `run`, which is itself redeclared each
   * render, so memoising it would only have produced a hook whose deps change every time.
   */
  function reviewProfile(
    venueId: string,
    approve: boolean,
    note: string | null,
    vis: Record<string, boolean> | null,
  ) {
    void run(async () => {
      const { error } = await supabase.rpc('admin_review_venue_profile', {
        p_venue_id: venueId,
        p_approve: approve,
        p_note: note,
        p_visibility: vis,
      });
      if (error) throw error;
      setOpenVenueId(null);
    }, approve ? 'Content approved' : 'Content rejected');
  }

  const blocked = sending?.blocked_reason ? BLOCKED_COPY[sending.blocked_reason] : null;
  const bounceAlarming = (counts?.bounce_rate ?? 0) > 5;

  return (
    <div className="admin-page mx-auto max-w-6xl">
      {toastNode}
      <PageHeader
        title="Venue Portal"
        subtitle="Outreach is cold email — getting venues onto the platform. CRM is everyone after they arrive: reached out, granted access, logged in. Partnerships live at the bottom of the CRM."
        actions={
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="admin-btn admin-btn-secondary admin-btn-sm"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        }
      />

      {loadError ? <ErrorBanner className="mb-4">{loadError}</ErrorBanner> : null}

      {/*
        ⚠️ VENUE ACTIVATION IS THE FIRST THING ON THE PAGE, ON EVERY TAB (Evan 2026-08-16: "ALL I WANT
        TO DO IS TO VERY SIMPLY SEARCH AND GIVE ACCESS TO VENUE PORTAL TO ONE VENUE… VENUE ACTIVATION
        SHOULD BE EASY AS FUCK"). It used to be a sub-block of the Portal-access section, several
        screens down and behind a tab — so the quickest, most-used action on the page was the hardest
        one to find. It is two fields and a button; it deserves the top.
      */}
      <div className="mb-6">
        <AccessGranter onGranted={() => void load()} />
      </div>

      <nav className="mb-7 flex gap-6 border-b border-border">
        {([
          /**
           * ⚠️ MESSAGES ARE STILL NOT A TAB (Evan 2026-08-16: "merge the CRM and inbox page"). A
           * message and a content submission from the same venue used to live on different tabs, so
           * acting on a venue meant remembering which half of it was where. Messages are a column in
           * CRM and a panel in the venue's drawer — the thread sits with the venue it belongs to.
           * That rule is untouched; Partnerships below is a third SUBJECT, not a second inbox.
           *
           * Partnerships (Evan 2026-09-22: *"put partnerships into venue portal. as a tab in venue
           * portal."*) — this screen is where he adds the venues he is engaging, funnels them into
           * the venue-facing portal, approves what they submit and manages their discounts. The
           * deals belong beside the relationship, not in a separate top-level tab.
           */
          ['crm', `CRM${venueRows.length ? ` (${venueRows.length})` : ''}${unreadTotal ? ` · ${unreadTotal} unread` : ''}`],
          ['promos', 'Promotions'],
          ['pipeline', 'Outreach'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setView(id)}
            className={`-mb-px border-b-2 px-0.5 pb-3 text-[13px] font-medium transition-colors ${
              view === id
                ? 'border-accent text-white'
                : 'border-transparent text-mutedDim hover:text-muted'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {view === 'crm' ? (
        <section className="space-y-6">
          {/*
            ⚠️ FILTERS ARE THE CRM (Evan 2026-08-16: "have the CRM page be filterable by important
            things like venue status"). The list is sorted by urgency already, but sorting only tells
            you what is first — a filter tells you what the whole list IS. Counts live on the chips so
            an empty category is visible before you click it.
          */}
          <div className="flex flex-wrap items-center gap-1.5">
            {CRM_FILTERS.map((f) => {
              const n = venueRows.filter(f.test).length;
              const active = crmFilter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setCrmFilter(f.id)}
                  className={`rounded-full border px-3 py-1.5 text-[12px] transition-colors ${
                    active
                      ? 'border-accent bg-accent/15 text-white'
                      : 'border-border text-mutedDim hover:text-muted'
                  }`}
                >
                  {f.label}
                  {n > 0 ? <span className="ml-1.5 text-mutedDim">{n}</span> : null}
                </button>
              );
            })}
            {/*
              ⚠️ THE OTHER AXIS (Evan 2026-09-28: *"we should be tracking distinct owners/managers
              that own/manage different places - add a button do do this to the crm page"*).
              Everything else on this page is one row per VENUE, so a group that runs four bars
              reads as four strangers. This button is the same relationships folded the other way —
              one card per person, their venues underneath. Read-only; see `VenuePeopleModal`.
            */}
            <button
              type="button"
              onClick={() => setPeopleOpen(true)}
              className="admin-btn admin-btn-secondary admin-btn-sm ml-auto"
            >
              People{peopleCount ? ` · ${peopleCount}` : ''}
            </button>
            <input
              value={crmQuery}
              onChange={(e) => setCrmQuery(e.target.value)}
              placeholder="Search venue, city or email"
              className="admin-input w-56 text-xs"
            />
            <label className="flex cursor-pointer items-center gap-2 text-[11px] text-mutedDim">
              <input
                type="checkbox"
                checked={showInterest}
                onChange={(e) => setShowInterest(e.target.checked)}
                className="accent-accent"
              />
              Feature interest
            </label>
          </div>

          {venueRows.length === 0 ? (
            <EmptyState title="No venues yet" hint="Find venues in the Outreach tab to get started." />
          ) : crmRows.length === 0 ? (
            <EmptyState
              compact
              title="Nothing matches"
              hint="No venue in the CRM is in that state right now."
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border bg-surface">
              <table className="w-full min-w-[860px] text-left text-xs">
                <thead className="border-b border-border text-[10px] uppercase tracking-[0.08em] text-mutedDim">
                  <tr>
                    <th className="px-4 py-2.5 font-semibold">Venue</th>
                    <th className="px-4 py-2.5 font-semibold">Outreach</th>
                    <th className="px-4 py-2.5 font-semibold">Portal</th>
                    <th className="px-4 py-2.5 font-semibold">Content</th>
                    {showInterest ? <th className="px-4 py-2.5 font-semibold">Wants</th> : null}
                    <th className="px-4 py-2.5 font-semibold">Messages</th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {crmRows.map((r) => (
                    <tr
                      key={r.venue_id}
                      onClick={() => setOpenVenueId(r.venue_id)}
                      className="cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-white/[0.03]"
                    >
                      <td className="px-4 py-3">
                        <div className="admin-label-primary truncate text-[13px]">{r.name}</div>
                        <div className="mt-0.5 truncate text-[11px] text-mutedDim">{r.city ?? ''}</div>
                      </td>
                      <td className="px-4 py-3">
                        {r.target ? <StatusPill status={r.target.status} /> : <span className="text-mutedDim">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {r.access ? (
                          <div>
                            <div className="text-muted">{r.access.email}</div>
                            <div className="mt-0.5 text-[10px] text-mutedDim">
                              {r.access.last_seen_at ? `seen ${timeAgo(r.access.last_seen_at)}` : 'never signed in'}
                              {r.access.can_post ? ' · can post' : ''}
                            </div>
                          </div>
                        ) : (
                          <span className="text-mutedDim">no access</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {r.profile ? (
                          <span
                            className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                              r.profile.status === 'pending'
                                ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                                : r.profile.status === 'approved'
                                  ? 'border-emerald-500/35 bg-emerald-500/10 text-emerald-200'
                                  : 'border-red-500/35 bg-red-500/10 text-red-200'
                            }`}
                          >
                            {r.profile.status === 'pending' ? 'review' : r.profile.status}
                          </span>
                        ) : (
                          <span className="text-mutedDim">—</span>
                        )}
                      </td>
                      {showInterest ? (
                        <td className="px-4 py-3">
                          {r.interests.length ? (
                            <div className="flex flex-wrap gap-1">
                              {r.interests.map((t) => (
                                <span key={t} className="rounded-full border border-accent/35 bg-accent/10 px-2 py-0.5 text-[10px] text-accent">
                                  {t}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-mutedDim">—</span>
                          )}
                        </td>
                      ) : null}
                      <td className="px-4 py-3">
                        {r.thread ? (
                          <span className={r.thread.unread ? 'text-accent' : 'text-mutedDim'}>
                            {r.thread.unread
                              ? `${r.thread.unread} unread`
                              : `${r.thread.total} message${r.thread.total === 1 ? '' : 's'}`}
                          </span>
                        ) : (
                          <span className="text-mutedDim">—</span>
                        )}
                      </td>
                      {/**
                       * ⚠️ THE RENDER PATH FOR `admin_open_venue_portal`. The RPC shipped applied to
                       * prod on 2026-09-28 (`20260928010000`) with **zero callers**, so Evan asked
                       * twice where the button was and was told it existed. It did not — only the
                       * plumbing did. This is the button.
                       *
                       * ⚠️ It is a REAL GRANT, not a preview: it inserts a `venue_portal_access` row
                       * for Evan against this venue, persisting until revoked. `_venue_portal_can_edit`
                       * is not touched — the grant works by adding a row to the table that function
                       * already reads, so the portal's one authorisation rule is unchanged.
                       *
                       * ⚠️ IT USED TO SAY "Get access" AND IT USED TO DROP HIM ON THE LOGIN SCREEN.
                       * The grant was real; the landing was not. `window.open` to another origin
                       * carries nothing, so the portal had no idea who had arrived — and Evan's admin
                       * identity (`evan@user.auth.shado`) is a synthetic address that can never
                       * receive the portal's 6-digit code, so there was no way for him to finish the
                       * sign-in by hand either. `openVenuePortal` hands the session across instead.
                       *
                       * `stopPropagation` because the whole row opens the drawer.
                       */}
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          className="admin-btn admin-btn-secondary admin-btn-sm"
                          disabled={busy}
                          onClick={(e) => {
                            e.stopPropagation();
                            /**
                             * ⚠️ THE TAB IS OPENED HERE, SYNCHRONOUSLY, INSIDE THE GESTURE — never
                             * after the `await`. A `window.open` that runs once the click has been
                             * forgotten is a pop-up, and Safari and Chrome block it. The old code
                             * opened it after the RPC resolved and got away with it only because the
                             * RPC is fast.
                             */
                            const handoff = openVenuePortal(r.venue_id);
                            void run(async () => {
                              const { error } = await supabase.rpc('admin_open_venue_portal', {
                                p_venue_id: r.venue_id,
                              });
                              if (error) {
                                handoff.abort();
                                throw error;
                              }
                              await handoff.handOffSession();
                            }, `Portal opened — ${r.name}`);
                          }}
                        >
                          Open venue portal
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/*
            Threads with no venue attached — a help-signing-in request from someone we cannot yet map
            to a row. They have nowhere else to live now that Inbox is gone, and dropping them would
            lose the exact messages from the people who most need an answer.
          */}
          {orphanThreads.length > 0 ? (
            <div>
              <SectionHeading
                title={`Unmatched messages · ${orphanThreads.length}`}
                hint="Messages from people we could not match to a venue — sign-in problems, venue issues, and signup requests."
              />
              <div className="space-y-2">
                {orphanThreads.map((t) => (
                  <InboxThread key={t.from_email} thread={t} onChanged={() => void load()} />
                ))}
              </div>
            </div>
          ) : null}

          {/*
            ⚠️ THESE WERE GOING NOWHERE (Evan 2026-08-17: "make sure these different things on the
            sign up page… still flow into the CRM"). `venue_signup_requests` had a table, a writer and
            an `admin_list_venue_signups` RPC — and NOTHING in the dashboard ever called it. Anyone
            who used "Join as an owner or manager" submitted into a black hole. Help / venue-problem
            messages were always fine: they land in `venue_messages` and surface below.
          */}
          {signups.filter((r) => r.status === 'pending').length > 0 ? (
            <div>
              <SectionHeading
                title={`Access requests · ${signups.filter((r) => r.status === 'pending').length}`}
                hint="People who asked for portal access from the sign-in page. Verify by hand, then grant access with the panel at the top."
              />
              <div className="space-y-2">
                {signups
                  .filter((r) => r.status === 'pending')
                  .map((r) => (
                    <div key={r.id} className="rounded-xl border border-amber-500/35 bg-amber-500/[0.05] p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="admin-label-primary truncate text-[14px]">
                            {r.venue_name ?? '—'}
                          </div>
                          <div className="mt-0.5 truncate text-[11px] text-mutedDim">
                            {r.venue_address ?? 'no address given'}
                          </div>
                          <div className="mt-1 text-[12px] text-muted">
                            {r.email}
                            {r.phone ? ` · ${r.phone}` : ''}
                          </div>
                          <div className="mt-0.5 text-[10px] text-mutedDim">{timeAgo(r.created_at)}</div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              void run(async () => {
                                const { error } = await supabase.rpc('admin_resolve_venue_signup', {
                                  p_id: r.id,
                                  p_status: 'approved',
                                  p_note: null,
                                });
                                if (error) throw error;
                              }, 'Marked handled — now grant access with the panel at the top')
                            }
                            className="admin-btn admin-btn-primary admin-btn-sm"
                          >
                            Mark handled
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              void run(async () => {
                                const note = window.prompt('Why is this being declined? (internal note)');
                                if (note === null) return;
                                const { error } = await supabase.rpc('admin_resolve_venue_signup', {
                                  p_id: r.id,
                                  p_status: 'rejected',
                                  p_note: note || null,
                                });
                                if (error) throw error;
                              }, 'Request declined')
                            }
                            className="text-[11px] text-mutedDim transition-colors hover:text-red-300"
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ) : null}

          {/*
            ⚠️ MOVED OUT OF PIPELINE (Evan 2026-08-16: "the pipeline page in admin dash still needs to
            be redone"). Portal access, claim requests and feature interest are all things that happen
            AFTER a venue arrives — they were sitting in the middle of the cold-outreach funnel, which
            made Pipeline nine steps long and buried the four that are actually the funnel. Pipeline is
            now exactly: find → email → approve → send → replies → problems.
          */}
      {/* Portal access — who can sign in as which venue */}
      <section className="mb-8">
        <SectionHeading
                    title={`Portal access${access.filter((a) => !a.revoked_at).length ? ` · ${access.filter((a) => !a.revoked_at).length}` : ''}`}
          hint="Who can sign in to the venue portal, and as which venue. Revoking takes effect immediately."
        />
        {/* Collapsed by default (Evan 2026-09-28: the page was "a horrible display of data") —
            the CRM list above IS the access list now; this is just the management controls. */}
        <button
          type="button"
          onClick={() => setManageAccess((v) => !v)}
          className="mb-3 text-[11px] text-mutedDim underline hover:text-muted"
        >
          {manageAccess ? 'Hide access management' : 'Manage access (emails, posting, revoke)'}
        </button>
        {manageAccess ? (
          <>

        <div className="mb-3">
          {/* The granter lives at the top of the page now — see the note there. */}
        </div>

        {access.length === 0 ? (
          <EmptyState compact title="Nobody has portal access yet" hint="Grant access above, or approve a claim." />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-surface">
            <table className="w-full min-w-[680px] text-left text-xs">
              <thead className="border-b border-border text-[10px] uppercase tracking-[0.08em] text-mutedDim">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Venue</th>
                  <th className="px-4 py-2.5 font-semibold">Signs in as</th>
                  <th className="px-4 py-2.5 font-semibold">Granted</th>
                  <th className="px-4 py-2.5 font-semibold">Last seen</th>
                  <th className="px-4 py-2.5 font-semibold">Can post</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {access.map((a) => (
                  <tr key={a.id} className={`border-b border-border/60 last:border-0 ${a.revoked_at ? 'opacity-45' : ''}`}>
                    <td className="px-4 py-2.5">
                      <div className="admin-label-primary truncate text-[13px]">{a.venue_name ?? '—'}</div>
                      <div className="mt-0.5 truncate text-[11px] text-mutedDim">{a.city_name ?? ''}</div>
                    </td>
                    <td className="px-4 py-2.5 text-muted">{a.email}</td>
                    <td className="px-4 py-2.5 text-mutedDim">{timeAgo(a.granted_at)}</td>
                    <td className="px-4 py-2.5 text-mutedDim">
                      {a.last_seen_at ? timeAgo(a.last_seen_at) : 'never signed in'}
                    </td>
                    <td className="px-4 py-2.5">
                      {a.revoked_at ? (
                        <span className="text-[11px] text-mutedDim">—</span>
                      ) : (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              const { error } = await supabase.rpc('admin_set_venue_can_post', {
                                p_access_id: a.id,
                                p_can_post: !a.can_post,
                              });
                              if (error) throw error;
                            }, a.can_post ? 'Posting turned off' : 'Posting turned on')
                          }
                          className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                            a.can_post
                              ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-200'
                              : 'border-border bg-white/[0.04] text-mutedDim hover:text-white'
                          }`}
                        >
                          {a.can_post ? 'On' : 'Off'}
                        </button>
                      )}
                    </td>
                    {/*
                      ⚠️ REVOKE IS TWO STEPS AND RESTORE IS ONE (Evan 2026-08-16: "I accidentally just
                      revoked portal access from the test room… make it harder to do that on
                      accident"). It sat one pixel-miss away from the posting toggle and fired
                      instantly. Revoking locks a real person out of their own venue page, so it now
                      asks; restoring is harmless, so it does not.
                    */}
                    <td className="px-4 py-2.5 text-right">
                      {a.revoked_at ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              const { error } = await supabase.rpc('admin_grant_venue_portal_access', {
                                p_venue_id: a.venue_id,
                                p_email: a.email,
                              });
                              if (error) throw error;
                            }, `Access restored for ${a.email}`)
                          }
                          className="text-[11px] text-accent transition-colors hover:text-white"
                        >
                          Restore access
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setConfirmRevoke(a)}
                          className="text-[11px] text-mutedDim transition-colors hover:text-red-300"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </>
        ) : null}
      </section>

      {claims.length > 0 ? (
        <section className="mb-8">
          <SectionHeading
                        title={`Claim requests · ${claims.length}`}
            hint="Submitted through the venue portal. Approving one means you're taking the relationship on."
          />
          <div className="space-y-2">
            {claims.map((c) => (
              <div
                key={c.id}
                className="rounded-xl border border-accent/40 bg-accent/[0.07] p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="admin-label-primary truncate text-[14px]">
                      {c.venue_name ?? '—'}
                      {c.city_name ? (
                        <span className="ml-2 text-[11px] font-normal text-mutedDim">{c.city_name}</span>
                      ) : null}
                    </div>
                    <div className="mt-1 text-[12px] text-muted">
                      {c.contact_name}
                      {c.role ? ` · ${c.role}` : ''} · {c.contact_email}
                    </div>
                    <div className="mt-0.5 text-[11px] text-mutedDim">{timeAgo(c.created_at)}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void run(async () => {
                          const { error } = await supabase.rpc('admin_review_venue_claim', {
                            p_claim_id: c.id,
                            p_approve: true,
                            p_note: null,
                          });
                          if (error) throw error;
                        }, `${c.venue_name ?? 'Venue'} approved`)
                      }
                      className="admin-btn admin-btn-primary admin-btn-sm"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void run(async () => {
                          const { error } = await supabase.rpc('admin_review_venue_claim', {
                            p_claim_id: c.id,
                            p_approve: false,
                            p_note: null,
                          });
                          if (error) throw error;
                        }, 'Claim rejected')
                      }
                      className="text-[11px] text-mutedDim transition-colors hover:text-white"
                    >
                      Reject
                    </button>
                  </div>
                </div>
                {c.message ? (
                  <p className="mt-3 rounded-lg border border-border bg-white/[0.03] px-3 py-2 text-[12px] leading-relaxed text-muted">
                    {c.message}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {showInterest && interest.length > 0 ? (
        <section className="mb-8">
          <SectionHeading
                        title={`Feature interest · ${interest.length}`}
            hint="Venues who asked for posting or partnership from the portal. This is the demand signal for whether either is worth building."
          />
          <div className="overflow-x-auto rounded-xl border border-border bg-surface">
            <table className="w-full min-w-[640px] text-left text-xs">
              <thead className="border-b border-border text-[10px] uppercase tracking-[0.08em] text-mutedDim">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Venue</th>
                  <th className="px-4 py-2.5 font-semibold">Wants</th>
                  <th className="px-4 py-2.5 font-semibold">Contact</th>
                  <th className="px-4 py-2.5 font-semibold">Note</th>
                  <th className="px-4 py-2.5 font-semibold">When</th>
                </tr>
              </thead>
              <tbody>
                {interest.map((i) => (
                  <tr key={`${i.venue_id}-${i.topic}`} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-2.5">
                      <div className="admin-label-primary truncate text-[13px]">{i.venue_name ?? '—'}</div>
                      <div className="mt-0.5 truncate text-[11px] text-mutedDim">{i.city_name ?? ''}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-accent">
                        {i.topic}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-muted">{i.email ?? '—'}</td>
                    <td className="px-4 py-2.5 text-muted">{i.note ?? '—'}</td>
                    <td className="px-4 py-2.5 text-mutedDim">{timeAgo(i.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
        </section>
      ) : null}

      {/* Promotions is its own tab again (Evan 2026-09-28: "the section with promotions should
          probably be its own tab next to CRM") — under-CRM lasted five days. */}
      {view === 'promos' ? <PartnershipsScreen embedded /> : null}

      {view === 'pipeline' ? (
      <>
      {/* Sending status — the single most important thing on the page */}
      <div
        className={`mb-5 rounded-xl border p-4 ${
          sending?.can_send ? 'border-emerald-500/40 bg-emerald-500/[0.06]' : 'border-border bg-surface'
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${
                  sending?.can_send ? 'bg-emerald-400' : 'bg-mutedDim'
                }`}
              />
              <span className="admin-label-primary text-[14px]">
                {sending?.can_send ? 'Sending is live' : (blocked?.label ?? 'Sending is off')}
              </span>
            </div>
            <p className="mt-1 text-[11px] leading-relaxed text-mutedDim">
              {sending?.can_send
                ? `${sending.remaining_today} of ${sending.daily_cap} left in today's warmup allowance.`
                : (blocked?.detail ?? 'Nothing can leave the database.')}
              {sending?.paused_reason ? ` — ${sending.paused_reason}` : ''}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {sending?.bounce_paused ? (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    const { error } = await supabase.rpc('admin_clear_outreach_bounce_pause');
                    if (error) throw error;
                  }, 'Delivery pause cleared')
                }
                className="admin-btn admin-btn-secondary admin-btn-sm"
              >
                Clear pause
              </button>
            ) : null}
            <button
              type="button"
              disabled={preflightBusy}
              onClick={() => void runPreflight()}
              className="admin-btn admin-btn-secondary admin-btn-sm"
            >
              {preflightBusy ? 'Checking…' : 'Run preflight'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirmSending(!(sending?.enabled ?? false))}
              className={`admin-btn admin-btn-sm ${
                sending?.enabled ? 'admin-btn-secondary' : 'admin-btn-primary'
              }`}
            >
              {sending?.enabled ? 'Turn sending off' : 'Turn sending on'}
            </button>
          </div>
        </div>

        {preflight ? (
          <div className="mt-3 border-t border-border/60 pt-3 text-[11px] leading-relaxed">
            <div className="admin-label-primary mb-1.5 text-[12px]">
              {preflight.ready
                ? 'Preflight passed — the sender is ready'
                : 'Preflight found something to fix'}
            </div>
            <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[auto_1fr]">
              <dt className="text-mutedDim">From</dt>
              <dd className="text-foreground">{preflight.sender.from}</dd>
              <dt className="text-mutedDim">Replies to</dt>
              <dd className="text-foreground">{preflight.sender.reply_to}</dd>
              <dt className="text-mutedDim">Portal link</dt>
              <dd className="text-foreground">{preflight.sender.claim_url ?? '—'}</dd>
              <dt className="text-mutedDim">Sending domain</dt>
              <dd className={preflight.domain.ok ? 'text-foreground' : 'text-red-400'}>
                {preflight.domain.domain} — {preflight.domain.status}
                {/*
                  "unchecked" is honest, not a pass. Our Resend key is send-only, so it is refused
                  by the /domains endpoint (401 restricted_api_key) and we cannot read verification
                  status. Saying "verified" here would be a guess presented as a check.
                */}
                {preflight.domain.detail ? (
                  <span className="text-mutedDim"> ({preflight.domain.detail})</span>
                ) : null}
              </dd>
            </dl>
            {preflight.missing.length > 0 ? (
              <p className="mt-2 text-red-400">
                Still missing in app_config.outreach_sender_identity:{' '}
                {preflight.missing.join(', ')}. CAN-SPAM requires a real postal address in every
                commercial email, so sending stays blocked until it is filled in.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {/*
        ⚠️ THREE NUMBERS, NOT FIVE (Evan 2026-08-16: "simplify the fuck out of the UI"). "Eligible
        pool" and "Suppressed" are facts about the corpus, not about today's work — they never change
        what you do next, so they moved to one line of text under the row.
      */}
      <div className="mb-3 grid grid-cols-3 gap-3">
        <StatCard label="In pipeline" value={targets.length} sub="live targets" />
        <StatCard
          label="Sent (24h)"
          value={sending?.sent_24h}
          sub={`cap ${sending?.daily_cap ?? '—'}`}
        />
        <StatCard
          label="Bounce rate"
          value={counts ? `${counts.bounce_rate}%` : null}
          sub={bounceAlarming ? 'Above the 5% auto-pause line' : 'Auto-pauses above 5%'}
          className={bounceAlarming ? 'border-red-500/50' : ''}
        />
      </div>
      <p className="mb-7 text-[11px] text-mutedDim">
        {counts?.eligible_pool ?? 0} eligible venues untouched · {counts?.sent_total ?? 0} emailed
        all-time · {counts?.suppressed ?? 0} suppressed
      </p>

      {/*
        ⚠️ ONE STEP AT A TIME (Evan 2026-08-16). All six sections rendered stacked, so the page was a
        3,000px scroll and the step you needed was never the one on screen. The funnel is a sequence;
        showing it as one is both simpler and truer to how it is worked.
      */}
      <div className="mb-6 flex flex-wrap gap-1.5">
        {PIPE_STEPS.map((st) => {
          const n = st.count(counts, targets, needsEmail, awaitingApproval, inFlight, replies, problems);
          const active = pipeStep === st.id;
          return (
            <button
              key={st.id}
              type="button"
              onClick={() => setPipeStep(st.id)}
              className={`rounded-full border px-3 py-1.5 text-[12px] transition-colors ${
                active
                  ? 'border-accent bg-accent/15 text-white'
                  : 'border-border text-mutedDim hover:text-muted'
              } ${st.alarm && n > 0 && !active ? 'border-red-500/40 text-red-300' : ''}`}
            >
              {st.id}. {st.label}
              {n > 0 ? <span className="ml-1.5 text-mutedDim">{n}</span> : null}
            </button>
          );
        })}
        {(() => {
          /* Clearing the funnel (Evan 2026-09-23: "make it easy to clear what's in the pipeline").
             Only the queue-like steps bulk-clear — Emails, Approve, Problems. In-flight and Replies
             are live sends and human answers; clearing those wholesale would be destructive. */
          const clearable: Record<number, Target[]> = { 2: needsEmail, 3: awaitingApproval, 6: problems };
          const list = clearable[pipeStep];
          if (!list || list.length === 0) return null;
          return (
            <button
              type="button"
              disabled={clearingStep}
              onClick={() => void clearStep(list)}
              className="ml-auto rounded-full border border-red-500/40 px-3 py-1.5 text-[12px] text-red-300 transition-colors hover:bg-red-500/10 disabled:opacity-50"
            >
              {clearingStep ? 'Clearing…' : `Clear these ${list.length}`}
            </button>
          );
        })()}
      </div>


      {pipeStep === 1 ? (
        <>
      {/*
        ⚠️ NO SINGLE-VENUE ADDER HERE (Evan 2026-08-16: "THE WHOLE PIPELINE THING IS ONLY FOR WHEN I
        AM SENDING OUT COLD OUTREACH BATCHES. NOT FOR WHEN I WANT TO SIMPLY ACTIVATE VENUES").
        Activating one venue is the panel pinned at the top of this page; it grants portal access
        directly and never touches the outreach funnel. `admin_add_outreach_target` still exists if a
        one-off cold target is ever wanted, but it is not surfaced.
      */}
      {/* 1 — Find venues */}
      <section className="mb-8">
        <SectionHeading
          step={1}
          title="Find venues"
          hint="Pulls eligible venues into the pipeline. Anything already targeted is excluded permanently."
          action={
            <>
              <select
                value={discoverScope}
                onChange={(e) => setDiscoverScope(e.target.value as 'launch' | 'all_us')}
                className="admin-input text-xs"
              >
                <option value="launch">Launch cities</option>
                <option value="all_us">All US</option>
              </select>
              <select
                value={discoverLimit}
                onChange={(e) => setDiscoverLimit(Number(e.target.value))}
                className="admin-input text-xs"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} at a time
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={discovering || loading}
                onClick={() => void discover()}
                className="admin-btn admin-btn-primary admin-btn-sm"
              >
                {discovering ? 'Finding…' : 'Find venues'}
              </button>
            </>
          }
        />
      </section>
        </>
      ) : null}

      {pipeStep === 2 ? (
        <>
      {/* 2 — Add contacts */}
      <section className="mb-8">
        <SectionHeading
          step={2}
          title={`Add emails${needsEmail.length ? ` · ${needsEmail.length} waiting` : ''}`}
          hint="Sourced by hand for now. Saving an email moves the venue on to be written."
        />
        {loading ? (
          <SkeletonRows rows={4} className="py-4" />
        ) : needsEmail.length === 0 ? (
          <EmptyState
            compact
            title="No venues waiting for an email"
            hint="Use Find venues above to pull more in."
          />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-surface">
            <table className="w-full min-w-[640px] text-left text-xs">
              <thead className="border-b border-border text-[10px] uppercase tracking-[0.08em] text-mutedDim">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Venue</th>
                  <th className="px-4 py-2.5 font-semibold">City</th>
                  <th className="px-4 py-2.5 font-semibold tabular-nums">Raters</th>
                  <th className="px-4 py-2.5 font-semibold">Email</th>
                </tr>
              </thead>
              <tbody>
                {needsEmail.map((t) => (
                  <tr key={t.id} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-2.5">
                      <div className="admin-label-primary truncate text-[13px]">{t.venue_name ?? '—'}</div>
                      {t.address ? (
                        <div className="mt-0.5 truncate text-[11px] text-mutedDim">{t.address}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-muted">{t.city_name ?? '—'}</td>
                    <td className="px-4 py-2.5 tabular-nums text-muted">{t.rater_count ?? 0}</td>
                    <td className="px-4 py-2.5">
                      <ContactCell target={t} onSaved={() => void load()} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
        </>
      ) : null}

      {pipeStep === 3 ? (
        <>
      {/* 3 — Review & approve */}
      <section className="mb-8">
        <SectionHeading
          step={3}
          title={`Review & approve${awaitingApproval.length ? ` · ${awaitingApproval.length} written` : ''}`}
          hint="Read every email before it goes out. Select the ones you're happy with, then approve them as a batch."
          action={
            selected.size > 0 ? (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    const label = `Batch ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`;
                    const { error } = await supabase.rpc('admin_create_outreach_batch', {
                      p_label: label,
                      p_target_ids: Array.from(selected),
                    });
                    if (error) throw error;
                  }, `Batch created from ${selected.size} email${selected.size === 1 ? '' : 's'}`)
                }
                className="admin-btn admin-btn-primary admin-btn-sm"
              >
                Batch {selected.size} selected
              </button>
            ) : undefined
          }
        />

        {readyToWrite.length > 0 ? (
          <div className="mb-3 rounded-lg border border-sky-500/30 bg-sky-500/[0.06] px-4 py-2.5 text-[11px] leading-relaxed text-sky-200">
            {readyToWrite.length} venue{readyToWrite.length === 1 ? ' has' : 's have'} an email address and
            {readyToWrite.length === 1 ? ' is' : ' are'} waiting to be written. The generator picks
            {readyToWrite.length === 1 ? ' it' : ' them'} up on its next run.
          </div>
        ) : null}

        {loading ? (
          <SkeletonRows rows={4} className="py-4" />
        ) : awaitingApproval.length === 0 ? (
          <EmptyState
            compact
            title="Nothing awaiting approval"
            hint="Emails appear here once the generator has written them."
          />
        ) : (
          <div className="space-y-2">
            {awaitingApproval.map((t) => (
              <div
                key={t.id}
                className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors ${
                  selected.has(t.id) ? 'border-accent/50 bg-accent/[0.07]' : 'border-border bg-surface'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(t.id)}
                  onChange={() => toggle(t.id)}
                  className="mt-1 accent-violet-500"
                  aria-label={`Select ${t.venue_name ?? 'venue'}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="admin-label-primary truncate text-[13px]">{t.venue_name ?? '—'}</span>
                    <span className="text-[11px] text-mutedDim">{t.city_name ?? ''}</span>
                    {t.variant ? (
                      <span className="rounded border border-border px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-mutedDim">
                        {t.variant}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 truncate text-[13px] text-white">{t.subject ?? '—'}</div>
                  <div className="mt-0.5 truncate text-[11px] text-mutedDim">To {t.email ?? '—'}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setPreview(t)}
                  className="admin-btn admin-btn-secondary admin-btn-sm shrink-0"
                >
                  Read
                </button>
              </div>
            ))}
          </div>
        )}

        {batches.length > 0 ? (
          <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-surface">
            <table className="w-full min-w-[640px] text-left text-xs">
              <thead className="border-b border-border text-[10px] uppercase tracking-[0.08em] text-mutedDim">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Batch</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold tabular-nums">Emails</th>
                  <th className="px-4 py-2.5 font-semibold tabular-nums">Sent</th>
                  <th className="px-4 py-2.5 font-semibold tabular-nums">Opened</th>
                  <th className="px-4 py-2.5 font-semibold tabular-nums">Replied</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-2.5 text-muted">{b.label}</td>
                    <td className="px-4 py-2.5">
                      <span className="rounded-full border border-border bg-white/[0.04] px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted">
                        {b.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-muted">{b.target_count}</td>
                    <td className="px-4 py-2.5 tabular-nums text-muted">{b.sent_count}</td>
                    <td className="px-4 py-2.5 tabular-nums text-muted">{b.opened_count}</td>
                    <td className="px-4 py-2.5 tabular-nums text-muted">{b.replied_count}</td>
                    <td className="px-4 py-2.5 text-right">
                      {b.status === 'draft' ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            disabled={busy || b.target_count === 0}
                            onClick={() => setConfirmApprove(b)}
                            className="admin-btn admin-btn-primary admin-btn-sm"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              void run(async () => {
                                const { error } = await supabase.rpc('admin_cancel_outreach_batch', {
                                  p_batch_id: b.id,
                                });
                                if (error) throw error;
                              }, 'Batch cancelled — emails returned to review')
                            }
                            className="text-[11px] text-mutedDim transition-colors hover:text-white"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
        </>
      ) : null}

      {pipeStep === 4 ? (
        <>
      {/* 4 — In flight */}
      <section className="mb-8">
        <SectionHeading
          step={4}
          title={`In flight${inFlight.length ? ` · ${inFlight.length}` : ''}`}
          hint="Approved and sent email. Opens and bounces arrive from the mail provider automatically."
        />
        {loading ? (
          <SkeletonRows rows={3} className="py-4" />
        ) : inFlight.length === 0 ? (
          <EmptyState compact title="Nothing in flight" hint="Approved emails appear here." />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-surface">
            <table className="w-full min-w-[680px] text-left text-xs">
              <thead className="border-b border-border text-[10px] uppercase tracking-[0.08em] text-mutedDim">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Venue</th>
                  <th className="px-4 py-2.5 font-semibold">Email</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold">Sent</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {inFlight.map((t) => (
                  <tr key={t.id} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-2.5">
                      <div className="admin-label-primary truncate text-[13px]">{t.venue_name ?? '—'}</div>
                      <div className="mt-0.5 truncate text-[11px] text-mutedDim">{t.city_name ?? ''}</div>
                    </td>
                    <td className="px-4 py-2.5 text-muted">{t.email ?? '—'}</td>
                    <td className="px-4 py-2.5">
                      <StatusPill status={t.status} />
                    </td>
                    <td className="px-4 py-2.5 text-mutedDim">{timeAgo(t.sent_at)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => setPreview(t)}
                        className="text-[11px] text-mutedDim transition-colors hover:text-white"
                      >
                        Read
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
        </>
      ) : null}

      {/* Venue-written content awaiting review — untrusted public input, read before approving */}
      {/*
        ⚠️ VENUE CONTENT REVIEW MOVED TO THE CRM TAB (Evan 2026-08-16: "on my admin dash, I do not
        see the ability to approve stuff on the CRM page"). Reviewing a venue's copy is something you
        do to a venue you already have a relationship with — it belongs beside their messages and
        their portal access, not in the middle of the cold-outreach funnel. Click the venue's row in
        CRM to read it, tick exactly what goes live, and approve.
      */}



      {/* Venues asking for capabilities we haven't built — the evidence for whether to build them */}


      {/* 7 — Claim requests: the end of the funnel, straight from the portal */}


      {pipeStep === 5 ? (
        <>
      {/* 6 — Replies */}
      <section className="mb-8">
        <SectionHeading
          step={5}
          title={`Replies${replies.length ? ` · ${replies.length}` : ''}`}
          hint="Every reply is flagged to you. Nothing is answered automatically — reply from your own inbox."
        />
        {replies.length === 0 ? (
          <EmptyState compact title="No replies yet" hint="Venue responses show up here." />
        ) : (
          <div className="space-y-2">
            {replies.map((r) => (
              <div
                key={r.id}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.05] p-3.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="admin-label-primary truncate text-[13px]">{r.venue_name ?? '—'}</div>
                  <div className="mt-0.5 truncate text-[11px] text-mutedDim">
                    {r.contact_name ? `${r.contact_name} · ` : ''}
                    {r.email} · replied {timeAgo(r.replied_at)}
                  </div>
                </div>
                {r.claimed_at ? (
                  <span className="rounded-full border border-emerald-400/60 bg-emerald-400/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-50">
                    Claimed
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        const { error } = await supabase.rpc('admin_mark_outreach_claimed', {
                          p_target_id: r.id,
                        });
                        if (error) throw error;
                      }, `${r.venue_name ?? 'Venue'} marked as claimed`)
                    }
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                  >
                    Mark claimed
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
        </>
      ) : null}

      {/* Problems */}
      {pipeStep === 6 ? (
        <>
      {problems.length > 0 ? (
        <section className="mb-8">
          <SectionHeading
            step={6}
            title={`Needs attention · ${problems.length}`}
            hint="Bounced, unsubscribed, or failed to send. Suppressed addresses are never contacted again."
          />
          <div className="overflow-x-auto rounded-xl border border-red-500/25 bg-surface">
            <table className="w-full min-w-[600px] text-left text-xs">
              <tbody>
                {problems.map((t) => (
                  <tr key={t.id} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-2.5">
                      <div className="admin-label-primary truncate text-[13px]">{t.venue_name ?? '—'}</div>
                      <div className="mt-0.5 truncate text-[11px] text-mutedDim">{t.email ?? '—'}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill status={t.status} />
                    </td>
                    <td className="px-4 py-2.5 text-[11px] text-red-300">
                      {t.bounce_reason ?? t.last_error ?? (t.unsubscribed ? 'unsubscribed' : '—')}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void run(async () => {
                            const { error } = await supabase.rpc('admin_supersede_outreach_target', {
                              p_target_id: t.id,
                              p_note: 'retired from dash',
                            });
                            if (error) throw error;
                          }, 'Target retired')
                        }
                        className="text-[11px] text-mutedDim transition-colors hover:text-white"
                        title="Retire this row so the venue can be re-targeted later"
                      >
                        Retire
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
        </>
      ) : null}

      {preview ? <EmailPreview target={preview} onClose={() => setPreview(null)} /> : null}

      </>
      ) : null}

      {/* People — the same relationships, grouped by human instead of by venue. Read-only; the only
          interaction is opening a venue's existing drawer. */}
      <VenuePeopleModal
        open={peopleOpen}
        rows={people}
        loading={loading}
        onClose={() => setPeopleOpen(false)}
        onOpenVenue={(venueId) => {
          setPeopleOpen(false);
          setOpenVenueId(venueId);
        }}
      />

      <ConfirmModal
        open={Boolean(confirmRevoke)}
        title="Revoke portal access?"
        message={
          confirmRevoke
            ? `${confirmRevoke.email} will be signed out of ${confirmRevoke.venue_name ?? 'this venue'} and can no longer edit or submit anything. Their already-approved content stays live, and you can restore access from the same row afterwards.`
            : ''
        }
        confirmLabel="Revoke access"
        danger
        onCancel={() => setConfirmRevoke(null)}
        onConfirm={() => {
          const target = confirmRevoke;
          setConfirmRevoke(null);
          if (!target) return;
          void run(async () => {
            const { error } = await supabase.rpc('admin_revoke_venue_portal_access', {
              p_access_id: target.id,
            });
            if (error) throw error;
          }, 'Access revoked');
        }}
      />

      <ConfirmModal
        open={Boolean(confirmApprove)}
        title="Approve this batch?"
        message={
          confirmApprove
            ? `${confirmApprove.target_count} email(s) become eligible to send. They go out gradually under the daily warmup cap — not all at once. Make sure you've read them.`
            : ''
        }
        confirmLabel="Approve batch"
        busy={busy}
        onConfirm={() =>
          void run(async () => {
            const { error } = await supabase.rpc('admin_approve_outreach_batch', {
              p_batch_id: confirmApprove!.id,
            });
            if (error) throw error;
            setConfirmApprove(null);
          }, 'Batch approved')
        }
        onCancel={() => setConfirmApprove(null)}
      />

      <ConfirmModal
        open={confirmSending !== null}
        title={confirmSending ? 'Turn sending on?' : 'Turn sending off?'}
        message={
          confirmSending
            ? 'Approved emails will start going out on the next scheduled run, up to the daily warmup cap. Only batches you have approved are affected.'
            : 'All sending stops immediately. Approved emails stay approved and resume when you turn it back on.'
        }
        confirmLabel={confirmSending ? 'Turn on' : 'Turn off'}
        danger={!confirmSending}
        busy={busy}
        onConfirm={() =>
          void run(async () => {
            const { error } = await supabase.rpc('admin_set_outreach_sending', {
              p_enabled: confirmSending,
              p_daily_cap: null,
            });
            if (error) throw error;
            setConfirmSending(null);
          }, confirmSending ? 'Sending is on' : 'Sending is off')
        }
        onCancel={() => setConfirmSending(null)}
      />

      {openVenue ? (
        <VenueDrawer
          row={openVenue}
          accessRows={access.filter((a) => a.venue_id === openVenue.venue_id)}
          busy={busy}
          onClose={() => setOpenVenueId(null)}
          onReview={reviewProfile}
          onGrant={(venueId, email) =>
            void run(async () => {
              const { error } = await supabase.rpc('admin_grant_venue_portal_access', {
                p_venue_id: venueId,
                p_email: email,
              });
              if (error) throw error;
            }, `Access granted to ${email}`)
          }
          onClearProfile={(venueId, venueName) => {
            if (
              !window.confirm(
                `Delete ALL submitted content for ${venueName}?\n\nDescription, hours, menus, photos, ` +
                  `door facts, specials and posts are removed and vanish from the venue page immediately. ` +
                  `The venue keeps portal access and can re-enter everything. This cannot be undone.`,
              )
            ) {
              return;
            }
            void run(async () => {
              const { error } = await supabase.rpc('admin_clear_venue_profile', { p_venue_id: venueId });
              if (error) throw error;
              setOpenVenueId(null);
            }, `Cleared all content for ${venueName}`);
          }}
          run={run}
          onRevoke={(a) => setConfirmRevoke(a)}
          onTogglePost={(a) =>
            void run(async () => {
              const { error } = await supabase.rpc('admin_set_venue_can_post', {
                p_access_id: a.id,
                p_can_post: !a.can_post,
              });
              if (error) throw error;
            }, a.can_post ? 'Posting turned off' : 'Posting turned on')
          }
          onChanged={() => void load()}
        />
      ) : null}
    </div>
  );
}
